import React, { useState, useEffect } from 'react';
import { adminAPI, payoutAPI } from '../../services/api';
import Navbar from '../../components/Navbar';
import './PayoutManagement.css';

const PayoutManagement = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState(null);
  const [payouts, setPayouts] = useState([]);
  const [restaurants, setRestaurants] = useState([]);
  const [deliveryPartners, setDeliveryPartners] = useState([]);
  const [selectedRecipient, setSelectedRecipient] = useState(null);
  const [showPayoutModal, setShowPayoutModal] = useState(false);
  const [showBankModal, setShowBankModal] = useState(false);
  const [payoutAmount, setPayoutAmount] = useState('');
  const [payoutDescription, setPayoutDescription] = useState('');
  const [bankDetails, setBankDetails] = useState({
    accountNumber: '',
    ifscCode: '',
    accountHolderName: '',
    bankName: ''
  });
  const [verifyingBankId, setVerifyingBankId] = useState(null);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [balance, setBalance] = useState(null);
  const [payoutMode, setPayoutMode] = useState(null);
  const [cashfreePayouts, setCashfreePayouts] = useState({ enabled: false, requireApproval: true });
  const [filters, setFilters] = useState({
    status: '',
    recipientType: '',
    page: 1
  });
  const [payoutSettings, setPayoutSettings] = useState({
    payoutDay: 1,
    payoutDayName: 'Monday',
    minimumPayoutAmount: 100,
    autoPayoutEnabled: false
  });
  const [dayOptions, setDayOptions] = useState([]);
  const [deliveryPayoutSchedule, setDeliveryPayoutSchedule] = useState(null);
  const [restaurantPayoutSchedule, setRestaurantPayoutSchedule] = useState(null);
  // Optional date range to scope "see & pay" to orders delivered in a window.
  const [payoutFrom, setPayoutFrom] = useState('');
  const [payoutTo, setPayoutTo] = useState('');
  const [payoutRangeActive, setPayoutRangeActive] = useState(false);
  const [groupedDeliveryPartners, setGroupedDeliveryPartners] = useState({
    ready: [],
    needs_bank_details: [],
    below_minimum: []
  });

  // Instant Payout Requests state
  const [instantPayoutRequests, setInstantPayoutRequests] = useState([]);
  const [instantPayoutStats, setInstantPayoutStats] = useState(null);
  const [instantPayoutSettings, setInstantPayoutSettings] = useState({
    instantPayoutFeePercent: 5,  // Default, will be fetched from backend
    platformGstRate: 18,         // Default, will be fetched from backend
    minPayoutAmount: 100,
    maxPayoutAmount: 50000
  });
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');

  // Settlement fee configuration. MUST mirror the backend payout flow exactly:
  //   gross → TDS (Section 194-O) deducted → settlement fee (3%) + GST (18%) on
  //   the AFTER-TDS amount → net payout.
  // The TDS step was previously missing here, so the modal showed a higher net
  // (e.g. ₹183.10) than the payout the server actually records (₹182.91).
  const SETTLEMENT_FEE_RATE = 3;
  const SETTLEMENT_FEE_GST_RATE = 18;
  const TDS_RATE_194O = 0.1; // 0.1% TDS u/s 194-O (reduced from 1% on 1 Oct 2024)

  // Calculate the full payout deduction breakdown for restaurant payouts. Mirrors the server
  // (payoutController): TDS always; then either the 3% settlement fee (subscription model, on
  // post-TDS) OR a flat platform-fee % on GROSS (commission model, no settlement fee).
  const calculateSettlementFee = (amount, billingModel) => {
    if (!amount || amount <= 0) {
      return { feeType: 'settlement', rate: SETTLEMENT_FEE_RATE, payoutAmount: 0, tds: 0, netAfterTds: 0, settlementFee: 0, gstOnFee: 0, cgst: 0, sgst: 0, totalFee: 0, totalDeducted: 0, netPayout: 0 };
    }
    const r2 = (n) => Math.round(n * 100) / 100; // round to paise (matches server)
    const payoutAmountNum = parseFloat(amount);
    const tds = r2(payoutAmountNum * TDS_RATE_194O / 100); // TDS on gross, always
    const netAfterTds = r2(payoutAmountNum - tds);

    if (billingModel && billingModel.type === 'commission') {
      const rate = Number(billingModel.commissionRate) || 10;
      const mode = billingModel.commissionGstMode || 'exclusive';
      let platformFee, gstOnFee, totalFee;
      if (mode === 'inclusive') {
        totalFee = r2(payoutAmountNum * rate / 100);                         // flat rate% of GROSS
        platformFee = r2(totalFee * 100 / (100 + SETTLEMENT_FEE_GST_RATE));
        gstOnFee = r2(totalFee - platformFee);
      } else {
        platformFee = r2(payoutAmountNum * rate / 100);                      // rate% of GROSS
        gstOnFee = r2(platformFee * SETTLEMENT_FEE_GST_RATE / 100);
        totalFee = r2(platformFee + gstOnFee);
      }
      const cgst = r2(gstOnFee / 2);
      const sgst = r2(gstOnFee - cgst);
      const netPayout = r2(payoutAmountNum - tds - totalFee);                // commission on GROSS
      return { feeType: 'commission', rate, gstMode: mode, payoutAmount: payoutAmountNum, tds, netAfterTds, settlementFee: platformFee, gstOnFee, cgst, sgst, totalFee, totalDeducted: r2(tds + totalFee), netPayout };
    }

    // Subscription model: 3% settlement fee + GST on the AFTER-TDS amount (existing).
    const settlementFee = r2(netAfterTds * SETTLEMENT_FEE_RATE / 100);
    const gstOnFee = r2(settlementFee * SETTLEMENT_FEE_GST_RATE / 100);
    const cgst = r2(gstOnFee / 2);
    const sgst = r2(gstOnFee - cgst);
    const totalFee = r2(settlementFee + gstOnFee);
    const netPayout = r2(netAfterTds - totalFee);
    const totalDeducted = r2(tds + totalFee);
    return { feeType: 'settlement', rate: SETTLEMENT_FEE_RATE, payoutAmount: payoutAmountNum, tds, netAfterTds, settlementFee, gstOnFee, cgst, sgst, totalFee, totalDeducted, netPayout };
  };

  // P2 Analytics, Predictions & Export state
  const [analyticsData, setAnalyticsData] = useState(null);
  const [earningsTrend, setEarningsTrend] = useState([]);
  const [payoutTrend, setPayoutTrend] = useState([]);
  const [payoutForecast, setPayoutForecast] = useState(null);
  const [analyticsPeriod, setAnalyticsPeriod] = useState('month');
  const [exportLoading, setExportLoading] = useState(false);

  // Order Financials state
  const [orderFinancials, setOrderFinancials] = useState([]);
  const [orderFinancialsSummary, setOrderFinancialsSummary] = useState(null);
  const [orderFinancialsLoading, setOrderFinancialsLoading] = useState(false);
  const [orderFinancialsPage, setOrderFinancialsPage] = useState(1);
  const [orderFinancialsPagination, setOrderFinancialsPagination] = useState(null);
  const [orderFinancialsSearch, setOrderFinancialsSearch] = useState('');
  const [expandedOrder, setExpandedOrder] = useState(null);

  // P2 Operations state
  const [operationsLoading, setOperationsLoading] = useState(false);
  const [failedPayouts, setFailedPayouts] = useState([]);
  const [failedPayoutsPagination, setFailedPayoutsPagination] = useState(null);
  const [selectedFailedPayouts, setSelectedFailedPayouts] = useState([]);
  const [retryStats, setRetryStats] = useState(null);
  const [circuitBreakerStatus, setCircuitBreakerStatus] = useState(null);
  const [webhookQueueStats, setWebhookQueueStats] = useState(null);
  const [pendingWebhooks, setPendingWebhooks] = useState([]);
  const [deadLetterQueue, setDeadLetterQueue] = useState([]);
  const [reconciliationStatus, setReconciliationStatus] = useState(null);
  const [reconciliationReports, setReconciliationReports] = useState([]);
  const [operationsSubTab, setOperationsSubTab] = useState('failedPayouts');

  // Financial Summary state (aggregate data)
  const [financialSummaryLoading, setFinancialSummaryLoading] = useState(false);
  const [financialSummary, setFinancialSummary] = useState(null);
  const [recentPayouts, setRecentPayouts] = useState([]);

  // Manual Payment Confirmation Modal state
  const [showManualPaymentModal, setShowManualPaymentModal] = useState(false);
  const [manualPaymentData, setManualPaymentData] = useState({
    payoutId: null,
    payout: null,
    utr: '',
    transferMode: 'imps',
    remarks: ''
  });
  const [confirmingPayment, setConfirmingPayment] = useState(false);

  useEffect(() => {
    fetchStats();
    fetchBalance();
    fetchPayoutMode();
    fetchPayoutSettings();
  }, []);

  const fetchPayoutMode = async () => {
    try {
      const response = await payoutAPI.getMode();
      setPayoutMode(response.data.data);
    } catch (error) {
      console.error('Failed to fetch payout mode:', error);
    }
  };

  const fetchPayoutSettings = async () => {
    try {
      const response = await payoutAPI.getPayoutSettings();
      const data = response.data.data;
      setPayoutSettings(data.payoutSettings);
      if (data.cashfreePayouts) setCashfreePayouts(data.cashfreePayouts);
      setDayOptions(data.dayOptions || []);
    } catch (error) {
      console.error('Failed to fetch payout settings:', error);
    }
  };

  const handleUpdatePayoutSettings = async () => {
    setLoading(true);
    try {
      await payoutAPI.updatePayoutSettings({
        payoutDay: payoutSettings.payoutDay,
        minimumPayoutAmount: payoutSettings.minimumPayoutAmount,
        autoPayoutEnabled: payoutSettings.autoPayoutEnabled,
        cashfreePayouts
      });
      setMessage({ type: 'success', text: 'Payout settings updated successfully!' });
      fetchPayoutSettings();
      fetchPayoutMode();
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.message || 'Failed to update settings' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'payouts') {
      fetchPayouts();
    } else if (activeTab === 'restaurants') {
      fetchRestaurants();
    } else if (activeTab === 'delivery') {
      fetchDeliveryPartners();
    } else if (activeTab === 'instant') {
      fetchInstantPayoutRequests();
    } else if (activeTab === 'orderFinancials') {
      fetchOrderFinancials();
    } else if (activeTab === 'financialSummary') {
      fetchFinancialSummary();
    }
  }, [activeTab, filters]);

  // Order Financials Functions
  const fetchOrderFinancials = async (page = orderFinancialsPage, search = orderFinancialsSearch) => {
    setOrderFinancialsLoading(true);
    try {
      const response = await payoutAPI.getOrderFinancials({
        page,
        limit: 15,
        search: search || undefined,
        hasEarnings: 'true'
      });
      const data = response.data.data;
      setOrderFinancials(data.orders || []);
      setOrderFinancialsSummary(data.summary || null);
      setOrderFinancialsPagination(data.pagination || null);
    } catch (error) {
      console.error('Failed to fetch order financials:', error);
      setMessage({ type: 'error', text: 'Failed to load order financials' });
    } finally {
      setOrderFinancialsLoading(false);
    }
  };

  const handleOrderFinancialsPageChange = (newPage) => {
    setOrderFinancialsPage(newPage);
    fetchOrderFinancials(newPage, orderFinancialsSearch);
  };

  const handleOrderFinancialsSearch = (e) => {
    e.preventDefault();
    setOrderFinancialsPage(1);
    fetchOrderFinancials(1, orderFinancialsSearch);
  };

  // Financial Summary Functions
  const fetchFinancialSummary = async () => {
    setFinancialSummaryLoading(true);
    try {
      // Fetch all required data in parallel
      const [
        restaurantsRes,
        deliveryPartnersRes,
        payoutsRes,
        instantRequestsRes,
        orderFinancialsRes
      ] = await Promise.all([
        payoutAPI.getRestaurantsForPayout().catch(() => ({ data: { data: [] } })),
        payoutAPI.getDeliveryPartnersForPayout().catch(() => ({ data: { data: [] } })),
        payoutAPI.getAllPayouts({ status: 'success', limit: 10 }).catch(() => ({ data: { data: { payouts: [] } } })),
        payoutAPI.getInstantPayoutRequests({ limit: 50 }).catch(() => ({ data: { data: { requests: [] } } })),
        payoutAPI.getOrderFinancials({ limit: 100, hasEarnings: 'true' }).catch(() => ({ data: { data: { summary: null } } }))
      ]);

      const allRestaurants = restaurantsRes.data?.data?.restaurants || [];
      const allDeliveryPartners = deliveryPartnersRes.data?.data?.deliveryPartners || [];
      const allPayouts = payoutsRes.data?.data?.payouts || [];
      const allInstantRequests = instantRequestsRes.data?.data?.requests || [];
      const orderSummary = orderFinancialsRes.data?.data?.summary || {};

      // Aggregate restaurant data
      const restaurantAggregates = {
        totalCount: allRestaurants.length,
        totalPendingBalance: allRestaurants.reduce((sum, r) => sum + (r.pendingAmount || 0), 0),
        totalNetEarnings: allRestaurants.reduce((sum, r) => sum + (r.availableBalance || r.pendingAmount || 0), 0),
        totalTdsDeducted: allRestaurants.reduce((sum, r) => sum + ((r.tdsAmount) || (r.earnings?.tdsDeducted) || 0), 0),
        readyForPayout: allRestaurants.filter(r => r.hasBankDetails && r.pendingAmount >= 100).length,
        needsBankDetails: allRestaurants.filter(r => !r.hasBankDetails).length,
        byStatus: {
          ready: allRestaurants.filter(r => r.hasBankDetails && r.pendingAmount >= 100),
          needsBank: allRestaurants.filter(r => !r.hasBankDetails),
          belowMin: allRestaurants.filter(r => r.hasBankDetails && r.pendingAmount < 100 && r.pendingAmount > 0)
        }
      };

      // Aggregate delivery partner data
      const deliveryAggregates = {
        totalCount: allDeliveryPartners.length,
        totalPendingBalance: allDeliveryPartners.reduce((sum, d) => sum + (d.pendingAmount || 0), 0),
        totalNetEarnings: allDeliveryPartners.reduce((sum, d) => sum + (d.availableBalance || d.pendingAmount || 0), 0),
        totalCommission: allDeliveryPartners.reduce((sum, d) => sum + ((d.earnings?.commissionPaid) || (d.pendingAmount * 0.1) || 0), 0),
        readyForPayout: allDeliveryPartners.filter(d => d.hasBankDetails && d.pendingAmount >= 100).length,
        needsBankDetails: allDeliveryPartners.filter(d => !d.hasBankDetails).length,
        byStatus: {
          ready: allDeliveryPartners.filter(d => d.hasBankDetails && d.pendingAmount >= 100),
          needsBank: allDeliveryPartners.filter(d => !d.hasBankDetails),
          belowMin: allDeliveryPartners.filter(d => d.hasBankDetails && d.pendingAmount < 100 && d.pendingAmount > 0)
        }
      };

      // Aggregate payout data
      const payoutAggregates = {
        totalPayouts: allPayouts.length,
        totalPaidOut: allPayouts.reduce((sum, p) => sum + (p.amount || 0), 0),
        restaurantPayouts: allPayouts.filter(p => p.recipientType === 'restaurant'),
        deliveryPayouts: allPayouts.filter(p => p.recipientType === 'delivery'),
        restaurantPaidOut: allPayouts.filter(p => p.recipientType === 'restaurant').reduce((sum, p) => sum + (p.amount || 0), 0),
        deliveryPaidOut: allPayouts.filter(p => p.recipientType === 'delivery').reduce((sum, p) => sum + (p.amount || 0), 0)
      };

      // Aggregate instant payout data
      const instantAggregates = {
        totalRequests: allInstantRequests.length,
        pending: allInstantRequests.filter(r => r.status === 'pending'),
        approved: allInstantRequests.filter(r => r.status === 'approved'),
        processing: allInstantRequests.filter(r => r.status === 'processing'),
        completed: allInstantRequests.filter(r => r.status === 'completed'),
        rejected: allInstantRequests.filter(r => r.status === 'rejected'),
        totalRequestedAmount: allInstantRequests.filter(r => ['pending', 'approved', 'processing', 'completed'].includes(r.status)).reduce((sum, r) => sum + (r.requestedAmount || 0), 0),
        totalFeesCollected: allInstantRequests.filter(r => r.status === 'completed').reduce((sum, r) => sum + (r.processingFee || 0), 0),
        totalNetPaid: allInstantRequests.filter(r => r.status === 'completed').reduce((sum, r) => sum + (r.netAmount || 0), 0),
        restaurantRequests: allInstantRequests.filter(r => r.recipientType === 'restaurant'),
        deliveryRequests: allInstantRequests.filter(r => r.recipientType === 'delivery')
      };

      setFinancialSummary({
        restaurants: restaurantAggregates,
        delivery: deliveryAggregates,
        payouts: payoutAggregates,
        instant: instantAggregates,
        orders: orderSummary,
        lastUpdated: new Date()
      });

      setRecentPayouts(allPayouts.slice(0, 10));
    } catch (error) {
      console.error('Failed to fetch financial summary:', error);
      setMessage({ type: 'error', text: 'Failed to load financial summary' });
    } finally {
      setFinancialSummaryLoading(false);
    }
  };

  // P2 Operations Functions
  const fetchOperationsData = async () => {
    setOperationsLoading(true);
    try {
      const [failedRes, retryRes, circuitRes, webhookRes, reconRes] = await Promise.all([
        payoutAPI.getFailedPayouts({ page: 1, limit: 20 }).catch(() => ({ data: { data: { payouts: [] } } })),
        payoutAPI.getRetryStats().catch(() => ({ data: { data: null } })),
        payoutAPI.getCircuitBreakerStatus().catch(() => ({ data: { data: null } })),
        payoutAPI.getWebhookQueueStats().catch(() => ({ data: { data: null } })),
        payoutAPI.getReconciliationStatus().catch(() => ({ data: { data: null } }))
      ]);

      setFailedPayouts(failedRes.data?.data?.payouts || []);
      setFailedPayoutsPagination(failedRes.data?.data?.pagination || null);
      setRetryStats(retryRes.data?.data || null);
      setCircuitBreakerStatus(circuitRes.data?.data || null);
      setWebhookQueueStats(webhookRes.data?.data || null);
      setReconciliationStatus(reconRes.data?.data || null);
    } catch (error) {
      console.error('Failed to fetch operations data:', error);
    } finally {
      setOperationsLoading(false);
    }
  };

  const fetchFailedPayouts = async (page = 1) => {
    setOperationsLoading(true);
    try {
      const response = await payoutAPI.getFailedPayouts({ page, limit: 20 });
      setFailedPayouts(response.data?.data?.payouts || []);
      setFailedPayoutsPagination(response.data?.data?.pagination || null);
    } catch (error) {
      console.error('Failed to fetch failed payouts:', error);
      setMessage({ type: 'error', text: 'Failed to load failed payouts' });
    } finally {
      setOperationsLoading(false);
    }
  };

  const handleBulkRetry = async () => {
    if (selectedFailedPayouts.length === 0) {
      setMessage({ type: 'error', text: 'Please select payouts to retry' });
      return;
    }
    if (!window.confirm(`Are you sure you want to retry ${selectedFailedPayouts.length} failed payouts?`)) {
      return;
    }
    setOperationsLoading(true);
    try {
      const response = await payoutAPI.bulkRetryPayouts(selectedFailedPayouts);
      const result = response.data?.data;
      setMessage({
        type: 'success',
        text: `Bulk retry initiated: ${result?.successful || 0} successful, ${result?.failed || 0} failed`
      });
      setSelectedFailedPayouts([]);
      fetchFailedPayouts();
      fetchOperationsData();
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.message || 'Failed to retry payouts' });
    } finally {
      setOperationsLoading(false);
    }
  };

  const handleResetCircuitBreaker = async () => {
    if (!window.confirm('Are you sure you want to reset the circuit breaker?')) {
      return;
    }
    setOperationsLoading(true);
    try {
      await payoutAPI.resetCircuitBreaker();
      setMessage({ type: 'success', text: 'Circuit breaker reset successfully' });
      fetchOperationsData();
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.message || 'Failed to reset circuit breaker' });
    } finally {
      setOperationsLoading(false);
    }
  };

  const fetchPendingWebhooks = async () => {
    setOperationsLoading(true);
    try {
      const response = await payoutAPI.getPendingWebhooks({ limit: 50 });
      setPendingWebhooks(response.data?.data?.webhooks || []);
    } catch (error) {
      console.error('Failed to fetch pending webhooks:', error);
    } finally {
      setOperationsLoading(false);
    }
  };

  const fetchDeadLetterQueue = async () => {
    setOperationsLoading(true);
    try {
      const response = await payoutAPI.getDeadLetterQueue({ limit: 50 });
      setDeadLetterQueue(response.data?.data?.webhooks || []);
    } catch (error) {
      console.error('Failed to fetch dead letter queue:', error);
    } finally {
      setOperationsLoading(false);
    }
  };

  const handleRetryWebhook = async (queueId) => {
    setOperationsLoading(true);
    try {
      await payoutAPI.retryWebhook(queueId);
      setMessage({ type: 'success', text: 'Webhook queued for retry' });
      fetchPendingWebhooks();
      fetchDeadLetterQueue();
      fetchOperationsData();
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.message || 'Failed to retry webhook' });
    } finally {
      setOperationsLoading(false);
    }
  };

  const handleProcessWebhookQueue = async () => {
    setOperationsLoading(true);
    try {
      const response = await payoutAPI.processWebhookQueue();
      const result = response.data?.data;
      setMessage({
        type: 'success',
        text: `Processed ${result?.processed || 0} webhooks`
      });
      fetchOperationsData();
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.message || 'Failed to process queue' });
    } finally {
      setOperationsLoading(false);
    }
  };

  const handleCleanupWebhookQueue = async (olderThanDays = 30) => {
    if (!window.confirm(`Are you sure you want to cleanup webhooks older than ${olderThanDays} days?`)) {
      return;
    }
    setOperationsLoading(true);
    try {
      const response = await payoutAPI.cleanupWebhookQueue({ olderThanDays });
      const result = response.data?.data;
      setMessage({
        type: 'success',
        text: `Cleaned up ${result?.deleted || 0} old webhooks`
      });
      fetchOperationsData();
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.message || 'Failed to cleanup queue' });
    } finally {
      setOperationsLoading(false);
    }
  };

  const handleRunReconciliation = async () => {
    if (!window.confirm('Are you sure you want to run reconciliation now? This may take a few minutes.')) {
      return;
    }
    setOperationsLoading(true);
    try {
      const response = await payoutAPI.runReconciliation({ force: true });
      const result = response.data?.data;
      setMessage({
        type: 'success',
        text: `Reconciliation completed. Checked: ${result?.summary?.totalChecked || 0}, Discrepancies: ${result?.summary?.totalDiscrepancies || 0}`
      });
      fetchOperationsData();
      fetchReconciliationReports();
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.message || 'Failed to run reconciliation' });
    } finally {
      setOperationsLoading(false);
    }
  };

  const fetchReconciliationReports = async () => {
    try {
      const response = await payoutAPI.getReconciliationReports({ limit: 10 });
      setReconciliationReports(response.data?.data?.reports || []);
    } catch (error) {
      console.error('Failed to fetch reconciliation reports:', error);
    }
  };

  const toggleFailedPayoutSelection = (payoutId) => {
    setSelectedFailedPayouts(prev =>
      prev.includes(payoutId)
        ? prev.filter(id => id !== payoutId)
        : [...prev, payoutId]
    );
  };

  const selectAllFailedPayouts = () => {
    if (selectedFailedPayouts.length === failedPayouts.length) {
      setSelectedFailedPayouts([]);
    } else {
      setSelectedFailedPayouts(failedPayouts.map(p => p._id));
    }
  };

  // Instant Payout Request Functions
  const fetchInstantPayoutRequests = async () => {
    setLoading(true);
    try {
      const response = await payoutAPI.getInstantPayoutRequests({ status: filters.status });
      setInstantPayoutRequests(response.data.data.requests || []);
      setInstantPayoutStats(response.data.data.stats || null);
      // Store centralized settings from backend
      if (response.data.data.settings) {
        setInstantPayoutSettings(prev => ({
          ...prev,
          ...response.data.data.settings
        }));
      }
    } catch (error) {
      console.error('Failed to fetch instant payout requests:', error);
      setMessage({ type: 'error', text: 'Failed to load instant payout requests' });
    } finally {
      setLoading(false);
    }
  };

  const handleApproveInstantPayout = async (requestId) => {
    if (!window.confirm('Are you sure you want to approve this instant payout request?')) {
      return;
    }
    setLoading(true);
    try {
      await payoutAPI.approveInstantPayoutRequest(requestId);
      setMessage({ type: 'success', text: 'Instant payout request approved successfully!' });
      fetchInstantPayoutRequests();
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.message || 'Failed to approve request' });
    } finally {
      setLoading(false);
    }
  };

  const handleRejectInstantPayout = async () => {
    if (!selectedRequest || !rejectionReason.trim()) {
      setMessage({ type: 'error', text: 'Please provide a rejection reason' });
      return;
    }
    setLoading(true);
    try {
      await payoutAPI.rejectInstantPayoutRequest(selectedRequest.requestId, rejectionReason);
      setMessage({ type: 'success', text: 'Instant payout request rejected successfully' });
      setShowRejectModal(false);
      setSelectedRequest(null);
      setRejectionReason('');
      fetchInstantPayoutRequests();
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.message || 'Failed to reject request' });
    } finally {
      setLoading(false);
    }
  };

  const openRejectModal = (request) => {
    setSelectedRequest(request);
    setRejectionReason('');
    setShowRejectModal(true);
  };

  const fetchStats = async () => {
    try {
      const response = await payoutAPI.getStats();
      setStats(response.data.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const fetchBalance = async () => {
    try {
      const response = await payoutAPI.getBalance();
      setBalance(response.data.data.balance);
    } catch (error) {
      console.error('Failed to fetch balance:', error);
    }
  };

  // P2: Fetch Analytics Data
  const fetchAnalyticsData = async () => {
    setLoading(true);
    try {
      const [dashboardRes, earningsRes, payoutRes, forecastRes] = await Promise.all([
        adminAPI.getFinancialDashboard({ period: analyticsPeriod }),
        adminAPI.getEarningsTrend({ period: analyticsPeriod }),
        adminAPI.getPayoutTrend({ period: analyticsPeriod }),
        adminAPI.getPayoutForecast()
      ]);
      setAnalyticsData(dashboardRes.data.data);
      setEarningsTrend(earningsRes.data.data || []);
      setPayoutTrend(payoutRes.data.data || []);
      setPayoutForecast(forecastRes.data.data);
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
      setMessage({ type: 'error', text: 'Failed to load analytics data' });
    } finally {
      setLoading(false);
    }
  };

  // P2: Export Functions
  const handleExport = async (exportType, params = {}) => {
    setExportLoading(true);
    try {
      let response;
      let filename;
      const format = params.format || 'csv';

      switch (exportType) {
        case 'platform-financials':
          response = await adminAPI.exportPlatformFinancials({ ...params, format });
          filename = `platform-financials-${new Date().toISOString().split('T')[0]}.${format}`;
          break;
        case 'payouts':
          response = await adminAPI.exportPayouts({ ...params, format });
          filename = `payouts-${new Date().toISOString().split('T')[0]}.${format}`;
          break;
        case 'tds-report':
          response = await adminAPI.exportTdsReport({ ...params, format });
          filename = `tds-report-${new Date().toISOString().split('T')[0]}.${format}`;
          break;
        case 'gst-report':
          response = await adminAPI.exportGstReport({ ...params, format });
          filename = `gst-report-${new Date().toISOString().split('T')[0]}.${format}`;
          break;
        default:
          throw new Error('Invalid export type');
      }

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      setMessage({ type: 'success', text: `${exportType} exported successfully!` });
    } catch (error) {
      console.error('Export failed:', error);
      setMessage({ type: 'error', text: 'Failed to export data' });
    } finally {
      setExportLoading(false);
    }
  };

  const fetchPayouts = async () => {
    setLoading(true);
    try {
      const response = await payoutAPI.getAllPayouts(filters);
      setPayouts(response.data.data.payouts);
    } catch (error) {
      console.error('Failed to fetch payouts:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchRestaurants = async (range) => {
    setLoading(true);
    try {
      const params = {};
      const from = range ? range.from : payoutFrom;
      const to = range ? range.to : payoutTo;
      if (from) params.startDate = from;
      if (to) params.endDate = to;
      const response = await payoutAPI.getRestaurantsForPayout(Object.keys(params).length ? params : undefined);
      const data = response.data.data;
      setRestaurants(data.restaurants);
      setPayoutRangeActive(!!data.dateRange);
      // Set payout schedule if available
      if (data.payoutSchedule) {
        setRestaurantPayoutSchedule(data.payoutSchedule);
      }
    } catch (error) {
      console.error('Failed to fetch restaurants:', error);
    } finally {
      setLoading(false);
    }
  };

  const applyPayoutRange = () => {
    if (!payoutFrom && !payoutTo) return;
    fetchRestaurants({ from: payoutFrom, to: payoutTo });
  };
  const clearPayoutRange = () => {
    setPayoutFrom('');
    setPayoutTo('');
    fetchRestaurants({ from: '', to: '' });
  };

  const fetchDeliveryPartners = async () => {
    setLoading(true);
    try {
      const response = await payoutAPI.getDeliveryPartnersForPayout();
      const data = response.data.data;

      // Handle both old format (deliveryPartners array) and new format (grouped)
      if (data.deliveryPartners && Array.isArray(data.deliveryPartners)) {
        // Old format - just set the array
        setDeliveryPartners(data.deliveryPartners);
        setGroupedDeliveryPartners({ ready: [], needs_bank_details: [], below_minimum: [] });
      } else {
        // New format with grouping
        setDeliveryPartners([
          ...(data.ready || []),
          ...(data.needs_bank_details || []),
          ...(data.below_minimum || [])
        ]);
        setGroupedDeliveryPartners({
          ready: data.ready || [],
          needs_bank_details: data.needs_bank_details || [],
          below_minimum: data.below_minimum || []
        });
      }

      // Set payout schedule if available
      if (data.payoutSchedule) {
        setDeliveryPayoutSchedule(data.payoutSchedule);
      }
    } catch (error) {
      console.error('Failed to fetch delivery partners:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePayout = async () => {
    if (!selectedRecipient || !payoutAmount) {
      setMessage({ type: 'error', text: 'Please enter payout amount' });
      return;
    }

    setLoading(true);
    try {
      let response;

      // Use subscription-aware endpoint for restaurants
      if (selectedRecipient.type === 'restaurant') {
        response = await payoutAPI.createRestaurantPayout({
          restaurantId: selectedRecipient._id,
          amount: parseFloat(payoutAmount),
          description: payoutDescription,
          deductSubscription: true // Enable auto-deduction of expired subscription
        });
      } else {
        response = await payoutAPI.createPayout({
          recipientType: selectedRecipient.type,
          recipientId: selectedRecipient._id,
          amount: parseFloat(payoutAmount),
          purpose: 'settlement',
          description: payoutDescription
        });
      }

      // Show settlement fee and subscription deduction info if applicable
      const settlementFeeInfo = response.data?.data?.settlementFeeInfo;
      const subscriptionDeduction = response.data?.data?.subscriptionDeduction;

      let successText = 'Payout created successfully!';

      if (settlementFeeInfo) {
        successText = `Payout created! Fee deducted: ₹${settlementFeeInfo.totalFee?.toFixed(2)} (${settlementFeeInfo.settlementFeeRate}% + GST). Net payout: ₹${settlementFeeInfo.netPayoutAfterFee?.toFixed(2)}`;
        if (settlementFeeInfo.invoiceNumber) {
          successText += `. Invoice: ${settlementFeeInfo.invoiceNumber}`;
        }
      }

      if (subscriptionDeduction?.success) {
        successText += ` | Subscription ₹${subscriptionDeduction.deductionPayout?.amount || 0} deducted.`;
      }

      setMessage({ type: 'success', text: successText });

      setShowPayoutModal(false);
      setPayoutAmount('');
      setPayoutDescription('');
      setSelectedRecipient(null);
      fetchStats();
      if (activeTab === 'restaurants') fetchRestaurants();
      if (activeTab === 'delivery') fetchDeliveryPartners();
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.message || 'Failed to create payout' });
    } finally {
      setLoading(false);
    }
  };

  // Open manual payment confirmation modal
  const handleProcessPayout = (payoutId, payout = null) => {
    setManualPaymentData({
      payoutId,
      payout,
      utr: '',
      transferMode: 'imps',
      remarks: ''
    });
    setShowManualPaymentModal(true);
  };

  // Auto-disburse via Cashfree Payouts (separate account). Gated on the backend by
  // Settings.cashfreePayouts.enabled + CASHFREE_PAYOUTS_* env — a clear error shows
  // if it's off. The transfer is async; status finalises via the Cashfree webhook.
  const handleDisbursePayout = async (payoutId, payout = null) => {
    const amt = payout?.amount ? `₹${payout.amount.toLocaleString()}` : 'this payout';
    const needsApproval = cashfreePayouts.requireApproval;
    const verb = needsApproval ? 'Approve and pay' : 'Auto-pay';
    if (!window.confirm(`${verb} ${amt} to ${payout?.recipientName || 'recipient'} via Cashfree Payouts?\n\nThis sends a real bank transfer. Final status arrives via webhook.`)) return;
    try {
      // When approval is required, this click IS the one-click approval (approve:true).
      const res = await payoutAPI.disbursePayout(payoutId, { approve: needsApproval });
      setMessage({ type: 'success', text: `Cashfree transfer initiated (${res.data?.data?.status || 'RECEIVED'}). It will complete via webhook.` });
      fetchPayouts();
      fetchStats();
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.message || 'Cashfree disbursement failed' });
    }
  };

  // Confirm manual payment with UTR
  const handleConfirmManualPayment = async () => {
    if (!manualPaymentData.utr.trim()) {
      setMessage({ type: 'error', text: 'UTR (Unique Transaction Reference) is required' });
      return;
    }

    setConfirmingPayment(true);
    try {
      await payoutAPI.processPayout(manualPaymentData.payoutId, {
        utr: manualPaymentData.utr.trim(),
        transferMode: manualPaymentData.transferMode,
        remarks: manualPaymentData.remarks
      });
      setMessage({ type: 'success', text: 'Payment confirmed successfully! Notification sent to recipient.' });
      setShowManualPaymentModal(false);
      setManualPaymentData({ payoutId: null, payout: null, utr: '', transferMode: 'imps', remarks: '' });
      fetchPayouts();
      fetchStats();
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.message || 'Failed to confirm payment' });
    } finally {
      setConfirmingPayment(false);
    }
  };

  // Copy text to clipboard
  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text).then(() => {
      setMessage({ type: 'success', text: `${label} copied to clipboard!` });
      setTimeout(() => setMessage({ type: '', text: '' }), 2000);
    }).catch(() => {
      setMessage({ type: 'error', text: 'Failed to copy' });
    });
  };

  const handleCancelPayout = async (payoutId) => {
    const reason = window.prompt('Please enter reason for cancellation:');
    if (!reason) return;

    setLoading(true);
    try {
      await payoutAPI.cancelPayout(payoutId, reason);
      setMessage({ type: 'success', text: 'Payout cancelled successfully!' });
      fetchPayouts();
      fetchStats();
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.message || 'Failed to cancel payout' });
    } finally {
      setLoading(false);
    }
  };

  const handleRetryPayout = async (payoutId) => {
    if (!window.confirm('Are you sure you want to retry this failed payout?')) {
      return;
    }

    setLoading(true);
    try {
      await payoutAPI.retryPayout(payoutId);
      setMessage({ type: 'success', text: 'Payout reset for retry. You can now process it again.' });
      fetchPayouts();
      fetchStats();
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.message || 'Failed to retry payout' });
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateBankDetails = async () => {
    if (!selectedRecipient) return;

    setLoading(true);
    try {
      await payoutAPI.updateBankDetails(
        selectedRecipient.type,
        selectedRecipient._id,
        bankDetails
      );
      setMessage({ type: 'success', text: 'Bank details updated successfully!' });
      setShowBankModal(false);
      setBankDetails({ accountNumber: '', ifscCode: '', accountHolderName: '', bankName: '' });
      if (activeTab === 'restaurants') fetchRestaurants();
      if (activeTab === 'delivery') fetchDeliveryPartners();
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.message || 'Failed to update bank details' });
    } finally {
      setLoading(false);
    }
  };

  // ==========================================
  // BANK VERIFICATION - DISABLED (Manual Payout Mode)
  // ==========================================
  const handleVerifyBank = async (entityId, entityType) => {
    // Bank verification is disabled in manual payout mode
    setMessage({
      type: 'info',
      text: 'Bank verification is disabled. Payouts are processed manually by confirming with UTR number.'
    });
  };

  const openPayoutModal = (recipient, type) => {
    setSelectedRecipient({ ...recipient, type });
    // Restaurants: pay out the GROSS pending (before TDS) so the server deducts
    // the 0.1% Section-194O TDS exactly once. The net `pendingAmount` already has
    // TDS removed, so sending it would double-deduct TDS (underpaying the
    // restaurant). Delivery partners have no payout-level TDS, so net is correct.
    const payable = type === 'restaurant'
      ? (recipient.grossPendingAmount ?? recipient.pendingAmount)
      : recipient.pendingAmount;
    setPayoutAmount(payable?.toString() || '');
    setShowPayoutModal(true);
  };

  const openBankModal = (recipient, type) => {
    setSelectedRecipient({ ...recipient, type });
    const existingBank = type === 'restaurant'
      ? recipient.bankDetails
      : recipient.deliveryPartner?.bankDetails;
    setBankDetails({
      accountNumber: existingBank?.accountNumber || '',
      ifscCode: existingBank?.ifscCode || '',
      accountHolderName: existingBank?.accountHolderName || recipient.name || '',
      bankName: existingBank?.bankName || ''
    });
    setShowBankModal(true);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-IN', {
      timeZone: 'Asia/Kolkata',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    const statusStyles = {
      pending: { bg: '#fff3cd', color: '#856404', icon: '⏳' },
      processing: { bg: '#cce5ff', color: '#004085', icon: '🔄' },
      success: { bg: '#d4edda', color: '#155724', icon: '✅' },
      failed: { bg: '#f8d7da', color: '#721c24', icon: '❌' },
      cancelled: { bg: '#e2e3e5', color: '#383d41', icon: '🚫' },
      reversed: { bg: '#fff3cd', color: '#856404', icon: '↩️' }
    };
    const style = statusStyles[status] || statusStyles.pending;
    return (
      <span className="status-badge" style={{ backgroundColor: style.bg, color: style.color }}>
        {style.icon} {status.toUpperCase()}
      </span>
    );
  };

  return (
    <div className="payout-management">
      <Navbar />
      <div className="container">
        {/* Test Mode Banner */}
        {payoutMode?.testMode && (
          <div className="test-mode-banner">
            <div className="test-mode-content">
              <span className="test-mode-icon">🧪</span>
              <div className="test-mode-info">
                <strong>TEST MODE ACTIVE</strong>
                <p>{payoutMode.info?.description}</p>
                {payoutMode.info?.tips && (
                  <ul className="test-mode-tips">
                    {payoutMode.info.tips.map((tip, i) => (
                      <li key={i}>{tip}</li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Header */}
        <div className="payout-header">
          <div>
            <h1>💰 Payout Management</h1>
            <p>Manage payouts to restaurants and delivery partners via Cashfree</p>
          </div>
          <div className="header-right">
            {payoutMode && (
              <span className={`mode-badge ${payoutMode.testMode ? 'test' : 'production'}`}>
                {payoutMode.testMode ? '🧪 TEST' : '🟢 LIVE'}
              </span>
            )}
            {balance && (
              <div className="balance-card">
                <span className="balance-label">Cashfree Balance</span>
                <span className="balance-amount">₹{balance.availableBalance?.toLocaleString() || '0'}</span>
              </div>
            )}
          </div>
        </div>

        {/* Message */}
        {message.text && (
          <div className={`alert alert-${message.type}`}>
            {message.text}
            <button onClick={() => setMessage({ type: '', text: '' })}>×</button>
          </div>
        )}

        {/* Stats Overview */}
        {stats && (
          <div className="stats-grid">
            <div className="stat-card">
              <div className="stat-icon">📊</div>
              <div className="stat-info">
                <span className="stat-value">
                  {stats.overview?.find(s => s._id === 'success')?.count || 0}
                </span>
                <span className="stat-label">Successful Payouts</span>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon">⏳</div>
              <div className="stat-info">
                <span className="stat-value">
                  {stats.overview?.find(s => s._id === 'pending')?.count || 0}
                </span>
                <span className="stat-label">Pending Payouts</span>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon">🏪</div>
              <div className="stat-info">
                <span className="stat-value">
                  ₹{(stats.restaurantPayouts?.find(s => s._id === 'success')?.totalAmount || 0).toLocaleString()}
                </span>
                <span className="stat-label">Restaurant Payouts</span>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon">🚴</div>
              <div className="stat-info">
                <span className="stat-value">
                  ₹{(stats.deliveryPayouts?.find(s => s._id === 'success')?.totalAmount || 0).toLocaleString()}
                </span>
                <span className="stat-label">Delivery Payouts</span>
              </div>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="tabs">
          <button
            className={`tab ${activeTab === 'overview' ? 'active' : ''}`}
            onClick={() => setActiveTab('overview')}
          >
            📊 Overview
          </button>
          <button
            className={`tab ${activeTab === 'restaurants' ? 'active' : ''}`}
            onClick={() => setActiveTab('restaurants')}
          >
            🏪 Restaurants
          </button>
          <button
            className={`tab ${activeTab === 'delivery' ? 'active' : ''}`}
            onClick={() => setActiveTab('delivery')}
          >
            🚴 Delivery Partners
          </button>
          <button
            className={`tab ${activeTab === 'payouts' ? 'active' : ''}`}
            onClick={() => setActiveTab('payouts')}
          >
            💳 All Payouts
          </button>
          <button
            className={`tab ${activeTab === 'settings' ? 'active' : ''}`}
            onClick={() => setActiveTab('settings')}
          >
            ⚙️ Settings
          </button>
          <button
            className={`tab ${activeTab === 'instant' ? 'active' : ''}`}
            onClick={() => setActiveTab('instant')}
          >
            ⚡ Instant Requests
          </button>
          <button
            className={`tab ${activeTab === 'analytics' ? 'active' : ''}`}
            onClick={() => { setActiveTab('analytics'); fetchAnalyticsData(); }}
          >
            📈 Analytics
          </button>
          <button
            className={`tab ${activeTab === 'exports' ? 'active' : ''}`}
            onClick={() => setActiveTab('exports')}
          >
            📥 Export
          </button>
          <button
            className={`tab ${activeTab === 'orderFinancials' ? 'active' : ''}`}
            onClick={() => { setActiveTab('orderFinancials'); fetchOrderFinancials(); }}
          >
            🧾 Order Financials
          </button>
          <button
            className={`tab ${activeTab === 'operations' ? 'active' : ''}`}
            onClick={() => { setActiveTab('operations'); fetchOperationsData(); }}
          >
            🔧 Operations
          </button>
          <button
            className={`tab ${activeTab === 'financialSummary' ? 'active' : ''}`}
            onClick={() => { setActiveTab('financialSummary'); fetchFinancialSummary(); }}
          >
            📊 Financial Summary
          </button>
        </div>

        {/* Tab Content */}
        <div className="tab-content">
          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div className="overview-content">
              <div className="info-card">
                <h3>🚀 Quick Actions</h3>
                <div className="quick-actions">
                  <button className="action-btn" onClick={() => setActiveTab('restaurants')}>
                    🏪 Pay Restaurants
                  </button>
                  <button className="action-btn" onClick={() => setActiveTab('delivery')}>
                    🚴 Pay Delivery Partners
                  </button>
                  <button className="action-btn" onClick={() => setActiveTab('payouts')}>
                    📋 View All Payouts
                  </button>
                </div>
              </div>
              <div className="info-card">
                <h3>ℹ️ How Payouts Work</h3>
                <ul>
                  <li>View pending amounts for restaurants and delivery partners</li>
                  <li>Ensure bank details are configured before creating payouts</li>
                  <li>Create payout records with the desired amount</li>
                  <li>Process payouts to initiate bank transfers via Cashfree</li>
                  <li>Track payout status in real-time</li>
                </ul>
              </div>
            </div>
          )}

          {/* Restaurants Tab */}
          {activeTab === 'restaurants' && (
            <div className="recipients-list">
              <h3>🏪 Restaurants with Pending Payouts</h3>

              {/* Date-range scope: see & pay only what was delivered in a window.
                  The payable is capped at each restaurant's true outstanding balance,
                  so paying window-by-window can never overpay. */}
              <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap', margin: '8px 0 16px', padding: '12px 14px', background: '#f8fafc', border: '1px solid #e5e7eb', borderRadius: 10 }}>
                <div>
                  <label style={{ display: 'block', fontSize: 12, color: '#6b7280' }}>From (delivered)</label>
                  <input type="date" value={payoutFrom} max={payoutTo || undefined} onChange={(e) => setPayoutFrom(e.target.value)} />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 12, color: '#6b7280' }}>To (delivered)</label>
                  <input type="date" value={payoutTo} min={payoutFrom || undefined} onChange={(e) => setPayoutTo(e.target.value)} />
                </div>
                <button className="btn btn-sm btn-primary" onClick={applyPayoutRange} disabled={!payoutFrom && !payoutTo}>Apply range</button>
                {payoutRangeActive && (
                  <button className="btn btn-sm btn-secondary" onClick={clearPayoutRange}>Clear (show all pending)</button>
                )}
                {payoutRangeActive ? (
                  <span style={{ fontSize: 12, color: '#047857', fontWeight: 600 }}>
                    📅 Showing orders delivered {payoutFrom || '…'} → {payoutTo || '…'}. "Payable" is capped at each restaurant's outstanding balance (no double-pay).
                  </span>
                ) : (
                  <span style={{ fontSize: 12, color: '#9ca3af' }}>No range = all-time pending. Pick dates to see & pay a specific window.</span>
                )}
              </div>

              {/* Payout Schedule Banner */}
              {restaurantPayoutSchedule && (
                <div className={`payout-schedule-banner ${restaurantPayoutSchedule.isPayoutDay ? 'payout-day' : ''}`}>
                  <div className="schedule-info">
                    <span className="schedule-icon">📅</span>
                    <div className="schedule-details">
                      <strong>
                        {restaurantPayoutSchedule.isPayoutDay
                          ? `🎉 Today is Payout Day (${restaurantPayoutSchedule.payoutDayName})!`
                          : `Weekly Payout: Every ${restaurantPayoutSchedule.payoutDayName}`}
                      </strong>
                      <p>{restaurantPayoutSchedule.message}</p>
                    </div>
                  </div>
                  <div className="schedule-meta">
                    <span className="minimum-badge">
                      Min: ₹{restaurantPayoutSchedule.minimumPayoutAmount}
                    </span>
                    {!restaurantPayoutSchedule.isPayoutDay && (
                      <span className="days-badge">
                        {restaurantPayoutSchedule.daysUntilNextPayout} day{restaurantPayoutSchedule.daysUntilNextPayout !== 1 ? 's' : ''} away
                      </span>
                    )}
                  </div>
                </div>
              )}

              {loading ? (
                <div className="loading">Loading restaurants...</div>
              ) : restaurants.length === 0 ? (
                <div className="empty-state">No restaurants with pending payouts</div>
              ) : (
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Restaurant</th>
                      <th>Contact</th>
                      <th>{payoutRangeActive ? 'Orders in window' : 'Pending Orders'}</th>
                      {payoutRangeActive && <th>Window Earnings</th>}
                      <th>{payoutRangeActive ? 'Payable now (capped)' : 'Pending Amount'}</th>
                      <th>Bank Details</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {restaurants.map(restaurant => (
                      <tr key={restaurant._id}>
                        <td>
                          <strong>{restaurant.name}</strong>
                        </td>
                        <td>
                          <div>{restaurant.email}</div>
                          <div className="text-muted">{restaurant.phone}</div>
                        </td>
                        <td>{restaurant.pendingOrders}</td>
                        {payoutRangeActive && (
                          <td className="amount" title="Gross (pre-TDS) earned from orders delivered in the selected window">
                            ₹{(restaurant.windowGross ?? 0).toLocaleString()}
                          </td>
                        )}
                        <td className="amount">
                          ₹{restaurant.pendingAmount?.toLocaleString()}
                          {payoutRangeActive && restaurant.availableBalance != null && (
                            <div className="text-muted" style={{ fontSize: 11 }}>
                              of ₹{restaurant.availableBalance?.toLocaleString()} total balance
                            </div>
                          )}
                        </td>
                        <td>
                          {restaurant.hasBankDetails ? (
                            <div className="bank-status-cell">
                              {/* Bank verification disabled - show bank details status only */}
                              <span className="badge badge-info" title="Bank details saved">
                                ✓ Bank Added
                              </span>
                              {/* Verify button hidden - manual payout mode */}
                            </div>
                          ) : (
                            <span className="badge badge-danger">❌ Not Set</span>
                          )}
                        </td>
                        <td>
                          <div className="action-buttons">
                            <button
                              className="btn btn-sm btn-primary"
                              onClick={() => openPayoutModal(restaurant, 'restaurant')}
                              disabled={!restaurant.hasBankDetails}
                              title={!restaurant.hasBankDetails ? 'Bank details required' : 'Create manual payout'}
                            >
                              💰 Pay
                            </button>
                            <button
                              className="btn btn-sm btn-secondary"
                              onClick={() => openBankModal(restaurant, 'restaurant')}
                            >
                              🏦 Bank
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}

          {/* Delivery Partners Tab */}
          {activeTab === 'delivery' && (
            <div className="recipients-list">
              <h3>🚴 Delivery Partners Payout</h3>

              {/* Payout Schedule Banner */}
              {deliveryPayoutSchedule && (
                <div className={`payout-schedule-banner ${deliveryPayoutSchedule.isPayoutDay ? 'payout-day' : ''}`}>
                  <div className="schedule-info">
                    <span className="schedule-icon">📅</span>
                    <div className="schedule-details">
                      <strong>
                        {deliveryPayoutSchedule.isPayoutDay
                          ? `🎉 Today is Payout Day (${deliveryPayoutSchedule.payoutDayName})!`
                          : `Weekly Payout: Every ${deliveryPayoutSchedule.payoutDayName}`}
                      </strong>
                      <p>{deliveryPayoutSchedule.message}</p>
                    </div>
                  </div>
                  <div className="schedule-meta">
                    <span className="minimum-badge">
                      Min: ₹{deliveryPayoutSchedule.minimumPayoutAmount}
                    </span>
                    {!deliveryPayoutSchedule.isPayoutDay && (
                      <span className="days-badge">
                        {deliveryPayoutSchedule.daysUntilNextPayout} day{deliveryPayoutSchedule.daysUntilNextPayout !== 1 ? 's' : ''} away
                      </span>
                    )}
                  </div>
                </div>
              )}

              {loading ? (
                <div className="loading">Loading delivery partners...</div>
              ) : deliveryPartners.length === 0 ? (
                <div className="empty-state">No delivery partners with pending payouts</div>
              ) : (
                <div className="grouped-partners">
                  {/* Ready for Payout */}
                  {groupedDeliveryPartners.ready.length > 0 && (
                    <div className="partner-group">
                      <div className="group-header ready">
                        <span className="group-icon">✅</span>
                        <span className="group-title">Ready for Payout</span>
                        <span className="group-count">{groupedDeliveryPartners.ready.length}</span>
                      </div>
                      <table className="data-table">
                        <thead>
                          <tr>
                            <th>Partner</th>
                            <th>Contact</th>
                            <th>Balance</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {groupedDeliveryPartners.ready.map(partner => (
                            <tr key={partner._id}>
                              <td><strong>{partner.name}</strong></td>
                              <td>
                                <div>{partner.email}</div>
                                <div className="text-muted">{partner.phone}</div>
                              </td>
                              <td className="amount">₹{(partner.balance || partner.pendingAmount)?.toLocaleString()}</td>
                              <td>
                                <div className="action-buttons">
                                  <button
                                    className="btn btn-sm btn-success"
                                    onClick={() => openPayoutModal(partner, 'delivery')}
                                  >
                                    💰 Pay Now
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {/* Needs Bank Details */}
                  {groupedDeliveryPartners.needs_bank_details.length > 0 && (
                    <div className="partner-group">
                      <div className="group-header warning">
                        <span className="group-icon">⚠️</span>
                        <span className="group-title">Needs Bank Details</span>
                        <span className="group-count">{groupedDeliveryPartners.needs_bank_details.length}</span>
                      </div>
                      <table className="data-table">
                        <thead>
                          <tr>
                            <th>Partner</th>
                            <th>Contact</th>
                            <th>Balance</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {groupedDeliveryPartners.needs_bank_details.map(partner => (
                            <tr key={partner._id}>
                              <td><strong>{partner.name}</strong></td>
                              <td>
                                <div>{partner.email}</div>
                                <div className="text-muted">{partner.phone}</div>
                              </td>
                              <td className="amount">₹{(partner.balance || partner.pendingAmount)?.toLocaleString()}</td>
                              <td>
                                <div className="action-buttons">
                                  <button
                                    className="btn btn-sm btn-warning"
                                    onClick={() => openBankModal(partner, 'delivery')}
                                  >
                                    🏦 Add Bank
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {/* Below Minimum */}
                  {groupedDeliveryPartners.below_minimum.length > 0 && (
                    <div className="partner-group">
                      <div className="group-header info">
                        <span className="group-icon">📊</span>
                        <span className="group-title">Below Minimum (₹{deliveryPayoutSchedule?.minimumPayoutAmount || 100})</span>
                        <span className="group-count">{groupedDeliveryPartners.below_minimum.length}</span>
                      </div>
                      <table className="data-table">
                        <thead>
                          <tr>
                            <th>Partner</th>
                            <th>Contact</th>
                            <th>Balance</th>
                            <th>Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {groupedDeliveryPartners.below_minimum.map(partner => (
                            <tr key={partner._id}>
                              <td><strong>{partner.name}</strong></td>
                              <td>
                                <div>{partner.email}</div>
                                <div className="text-muted">{partner.phone}</div>
                              </td>
                              <td className="amount">₹{(partner.balance || partner.pendingAmount)?.toLocaleString()}</td>
                              <td>
                                <span className="badge badge-info">
                                  Need ₹{((deliveryPayoutSchedule?.minimumPayoutAmount || 100) - (partner.balance || partner.pendingAmount || 0)).toLocaleString()} more
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {/* Fallback to old format if no grouped data */}
                  {groupedDeliveryPartners.ready.length === 0 &&
                   groupedDeliveryPartners.needs_bank_details.length === 0 &&
                   groupedDeliveryPartners.below_minimum.length === 0 &&
                   deliveryPartners.length > 0 && (
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>Partner</th>
                          <th>Contact</th>
                          <th>Pending Deliveries</th>
                          <th>Pending Amount</th>
                          <th>Bank Details</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {deliveryPartners.map(partner => (
                          <tr key={partner._id}>
                            <td><strong>{partner.name}</strong></td>
                            <td>
                              <div>{partner.email}</div>
                              <div className="text-muted">{partner.phone}</div>
                            </td>
                            <td>{partner.pendingOrders}</td>
                            <td className="amount">₹{partner.pendingAmount?.toLocaleString()}</td>
                            <td>
                              {partner.hasBankDetails ? (
                                <div className="bank-status-cell">
                                  {/* Bank verification disabled - show bank details status only */}
                                  <span className="badge badge-info" title="Bank details saved">
                                    ✓ Bank Added
                                  </span>
                                  {/* Verify button hidden - manual payout mode */}
                                </div>
                              ) : (
                                <span className="badge badge-danger">❌ Not Set</span>
                              )}
                            </td>
                            <td>
                              <div className="action-buttons">
                                <button
                                  className="btn btn-sm btn-primary"
                                  onClick={() => openPayoutModal(partner, 'delivery')}
                                  disabled={!partner.hasBankDetails}
                                  title={!partner.hasBankDetails ? 'Bank details required' : 'Create manual payout'}
                                >
                                  💰 Pay
                                </button>
                                <button
                                  className="btn btn-sm btn-secondary"
                                  onClick={() => openBankModal(partner, 'delivery')}
                                >
                                  🏦 Bank
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              )}
            </div>
          )}

          {/* All Payouts Tab */}
          {activeTab === 'payouts' && (
            <div className="payouts-list">
              <div className="filters">
                <select
                  value={filters.status}
                  onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                >
                  <option value="">All Status</option>
                  <option value="pending">Pending</option>
                  <option value="processing">Processing</option>
                  <option value="success">Success</option>
                  <option value="failed">Failed</option>
                  <option value="cancelled">Cancelled</option>
                </select>
                <select
                  value={filters.recipientType}
                  onChange={(e) => setFilters({ ...filters, recipientType: e.target.value })}
                >
                  <option value="">All Types</option>
                  <option value="restaurant">Restaurants</option>
                  <option value="delivery">Delivery Partners</option>
                </select>
                <button className="btn btn-secondary" onClick={fetchPayouts}>
                  🔄 Refresh
                </button>
              </div>

              {loading ? (
                <div className="loading">Loading payouts...</div>
              ) : payouts.length === 0 ? (
                <div className="empty-state">No payouts found</div>
              ) : (
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Payout ID</th>
                      <th>Recipient</th>
                      <th>Type</th>
                      <th>Amount</th>
                      <th>Status</th>
                      <th>Created</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {payouts.map(payout => (
                      <tr key={payout._id}>
                        <td className="mono">{payout.payoutId}</td>
                        <td>
                          <strong>{payout.recipientName}</strong>
                          <div className="text-muted">{payout.recipientEmail}</div>
                        </td>
                        <td>
                          {payout.recipientType === 'restaurant' ? '🏪' : '🚴'}{' '}
                          {payout.recipientType}
                        </td>
                        <td className="amount">₹{payout.amount?.toLocaleString()}</td>
                        <td>{getStatusBadge(payout.status)}</td>
                        <td>{formatDate(payout.createdAt)}</td>
                        <td>
                          <div className="action-buttons">
                            {payout.status === 'pending' && (
                              <>
                                <button
                                  className="btn btn-sm btn-success"
                                  onClick={() => handleProcessPayout(payout._id, payout)}
                                  title="Confirm manual bank transfer"
                                >
                                  ✓ Confirm Payment
                                </button>
                                {cashfreePayouts.enabled && (
                                  <button
                                    className="btn btn-sm btn-primary"
                                    onClick={() => handleDisbursePayout(payout._id, payout)}
                                    title={cashfreePayouts.requireApproval ? 'Approve & pay this transfer via Cashfree' : 'Auto-pay via Cashfree Payouts'}
                                  >
                                    {cashfreePayouts.requireApproval ? '✅ Approve & Pay' : '⚡ Pay via Cashfree'}
                                  </button>
                                )}
                                <button
                                  className="btn btn-sm btn-danger"
                                  onClick={() => handleCancelPayout(payout._id)}
                                >
                                  ❌ Cancel
                                </button>
                              </>
                            )}
                            {payout.status === 'failed' && (
                              <button
                                className="btn btn-sm btn-warning"
                                onClick={() => handleRetryPayout(payout._id)}
                              >
                                🔄 Retry
                              </button>
                            )}
                            {payout.status === 'success' && payout.utr && (
                              <span className="utr-badge">UTR: {payout.utr}</span>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}

          {/* Settings Tab */}
          {activeTab === 'settings' && (
            <div className="settings-content">
              <div className="settings-card">
                <h3>📅 Weekly Payout Schedule</h3>
                <p className="settings-description">
                  Configure when delivery partners receive their weekly payouts.
                </p>

                <div className="settings-form">
                  <div className="form-group">
                    <label>Payout Day</label>
                    <select
                      value={payoutSettings.payoutDay}
                      onChange={(e) => setPayoutSettings({
                        ...payoutSettings,
                        payoutDay: parseInt(e.target.value)
                      })}
                    >
                      {dayOptions.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                    <span className="form-hint">
                      Payouts will be processed every {payoutSettings.payoutDayName || 'Monday'}
                    </span>
                  </div>

                  <div className="form-group">
                    <label>Minimum Payout Amount (₹)</label>
                    <input
                      type="number"
                      value={payoutSettings.minimumPayoutAmount}
                      onChange={(e) => setPayoutSettings({
                        ...payoutSettings,
                        minimumPayoutAmount: parseInt(e.target.value) || 0
                      })}
                      min="0"
                      placeholder="100"
                    />
                    <span className="form-hint">
                      Partners must earn at least ₹{payoutSettings.minimumPayoutAmount} to receive payout
                    </span>
                  </div>

                  <div className="form-group checkbox-group">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={payoutSettings.autoPayoutEnabled}
                        onChange={(e) => setPayoutSettings({
                          ...payoutSettings,
                          autoPayoutEnabled: e.target.checked
                        })}
                      />
                      <span>Enable Auto-Payout</span>
                    </label>
                    <span className="form-hint">
                      Automatically process payouts on the scheduled day (coming soon)
                    </span>
                  </div>

                  <div className="settings-actions">
                    <button
                      className="btn btn-primary"
                      onClick={handleUpdatePayoutSettings}
                      disabled={loading}
                    >
                      {loading ? 'Saving...' : '💾 Save Settings'}
                    </button>
                  </div>
                </div>
              </div>

              <div className="settings-card">
                <h3>⚡ Cashfree Payouts (Auto-disbursement)</h3>
                <p className="settings-description">
                  Pay restaurants/riders directly to their bank via Cashfree. When off, payouts stay manual (Confirm Payment + UTR).
                </p>
                <div className="settings-form">
                  <div className="form-group checkbox-group">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={cashfreePayouts.enabled}
                        onChange={(e) => setCashfreePayouts({ ...cashfreePayouts, enabled: e.target.checked })}
                      />
                      <span>Enable Cashfree auto-payout</span>
                    </label>
                    <span className="form-hint">
                      Requires CASHFREE_PAYOUTS_* on the server {payoutMode?.cashfreePayouts?.configured ? '(configured ✓)' : '(not configured ✗)'}. Off → manual payouts only.
                    </span>
                  </div>

                  <div className="form-group checkbox-group">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={cashfreePayouts.requireApproval}
                        onChange={(e) => setCashfreePayouts({ ...cashfreePayouts, requireApproval: e.target.checked })}
                      />
                      <span>Require one-click admin approval per transfer</span>
                    </label>
                    <span className="form-hint">
                      When ON, every transfer needs an explicit admin approval click before money is sent (button shows “Approve &amp; Pay”). When OFF, the pay button disburses directly.
                    </span>
                  </div>

                  <div className="settings-actions">
                    <button className="btn btn-primary" onClick={handleUpdatePayoutSettings} disabled={loading}>
                      {loading ? 'Saving...' : '💾 Save Cashfree Settings'}
                    </button>
                  </div>
                </div>
              </div>

              <div className="settings-card info-card">
                <h3>ℹ️ How Weekly Payouts Work</h3>
                <ul>
                  <li>Delivery partners see the configured payout day in their app</li>
                  <li>On payout day, partners with balances above the minimum will be prioritized</li>
                  <li>Partners without bank details will be flagged for follow-up</li>
                  <li>You can manually process payouts from the Delivery Partners tab</li>
                </ul>
              </div>
            </div>
          )}

          {/* Instant Payout Requests Tab */}
          {activeTab === 'instant' && (
            <div className="instant-payout-content">
              {/* Stats Cards */}
              {instantPayoutStats && (
                <div className="instant-stats-grid">
                  <div className="stat-card warning">
                    <div className="stat-icon">⏳</div>
                    <div className="stat-info">
                      <span className="stat-value">{instantPayoutStats.pending || 0}</span>
                      <span className="stat-label">Pending</span>
                    </div>
                  </div>
                  <div className="stat-card success">
                    <div className="stat-icon">✅</div>
                    <div className="stat-info">
                      <span className="stat-value">{instantPayoutStats.approved || 0}</span>
                      <span className="stat-label">Approved</span>
                    </div>
                  </div>
                  <div className="stat-card danger">
                    <div className="stat-icon">❌</div>
                    <div className="stat-info">
                      <span className="stat-value">{instantPayoutStats.rejected || 0}</span>
                      <span className="stat-label">Rejected</span>
                    </div>
                  </div>
                  <div className="stat-card info">
                    <div className="stat-icon">💰</div>
                    <div className="stat-info">
                      <span className="stat-value">₹{(instantPayoutStats.totalAmount || 0).toLocaleString()}</span>
                      <span className="stat-label">Total Requested</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Filter */}
              <div className="filter-bar">
                <select
                  value={filters.status}
                  onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                >
                  <option value="">All Status</option>
                  <option value="pending">Pending</option>
                  <option value="approved">Approved</option>
                  <option value="rejected">Rejected</option>
                  <option value="completed">Completed</option>
                </select>
                <button className="btn btn-secondary" onClick={fetchInstantPayoutRequests}>
                  🔄 Refresh
                </button>
              </div>

              {/* Requests Table */}
              <div className="recipients-list">
                <h3>⚡ Instant Payout Requests</h3>
                {loading ? (
                  <div className="loading">Loading requests...</div>
                ) : instantPayoutRequests.length === 0 ? (
                  <div className="empty-state">No instant payout requests found</div>
                ) : (
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Request ID</th>
                        <th>Partner</th>
                        <th>Requested</th>
                        <th>Fee ({instantPayoutSettings.instantPayoutFeePercent}%)</th>
                        <th>Net Amount</th>
                        <th>Bank Details</th>
                        <th>Status</th>
                        <th>Date</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {instantPayoutRequests.map(request => (
                        <tr key={request.requestId}>
                          <td><code>{request.requestId}</code></td>
                          <td>
                            <strong>{request.partnerName}</strong>
                            <div className="text-muted">{request.partnerPhone}</div>
                          </td>
                          <td className="amount">₹{request.requestedAmount?.toLocaleString()}</td>
                          <td className="fee">- ₹{request.processingFee?.toLocaleString()}</td>
                          <td className="amount success">₹{request.netAmount?.toLocaleString()}</td>
                          <td>
                            {request.bankDetails?.accountNumber ? (
                              <div className="bank-info">
                                <span className="badge badge-success">✅ Available</span>
                                <div className="text-muted small">
                                  A/C: ****{request.bankDetails.accountNumber.slice(-4)}
                                </div>
                              </div>
                            ) : (
                              <span className="badge badge-warning">⚠️ Missing</span>
                            )}
                          </td>
                          <td>
                            {request.status === 'pending' && (
                              <span className="badge badge-warning">⏳ Pending</span>
                            )}
                            {request.status === 'approved' && (
                              <span className="badge badge-success">✅ Approved</span>
                            )}
                            {request.status === 'rejected' && (
                              <span className="badge badge-danger" title={request.rejectionReason}>
                                ❌ Rejected
                              </span>
                            )}
                            {request.status === 'processing' && (
                              <span className="badge badge-info">🔄 Processing</span>
                            )}
                            {request.status === 'completed' && (
                              <span className="badge badge-success">✅ Completed</span>
                            )}
                            {request.status === 'failed' && (
                              <span className="badge badge-danger">❌ Failed</span>
                            )}
                          </td>
                          <td>{formatDate(request.createdAt)}</td>
                          <td>
                            {request.status === 'pending' && (
                              <div className="action-buttons">
                                <button
                                  className="btn btn-success btn-sm"
                                  onClick={() => handleApproveInstantPayout(request.requestId)}
                                  disabled={loading}
                                  title="Approve and create payout"
                                >
                                  ✅ Approve
                                </button>
                                <button
                                  className="btn btn-danger btn-sm"
                                  onClick={() => openRejectModal(request)}
                                  disabled={loading}
                                  title="Reject request"
                                >
                                  ❌ Reject
                                </button>
                              </div>
                            )}
                            {request.status === 'rejected' && request.rejectionReason && (
                              <span className="text-muted small" title={request.rejectionReason}>
                                📝 {request.rejectionReason.substring(0, 20)}...
                              </span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>

              {/* Info Card */}
              <div className="info-card">
                <h3>ℹ️ About Instant Payouts</h3>
                <ul>
                  <li>Delivery partners can request instant payouts with a {instantPayoutSettings.instantPayoutFeePercent}% processing fee</li>
                  <li>Minimum instant payout amount is ₹100</li>
                  <li>When approved, the net amount (after fee deduction) is transferred to their bank account</li>
                  <li>Rejected requests refund the full amount to the partner's pending balance</li>
                </ul>
              </div>
            </div>
          )}

          {/* P2: Analytics Tab */}
          {activeTab === 'analytics' && (
            <div className="analytics-content">
              <div className="analytics-header">
                <h3>📈 Financial Analytics & Predictions</h3>
                <div className="period-selector">
                  <select
                    value={analyticsPeriod}
                    onChange={(e) => {
                      setAnalyticsPeriod(e.target.value);
                      fetchAnalyticsData();
                    }}
                  >
                    <option value="week">Last Week</option>
                    <option value="month">Last Month</option>
                    <option value="quarter">Last Quarter</option>
                    <option value="year">Last Year</option>
                  </select>
                  <button className="btn btn-secondary" onClick={fetchAnalyticsData}>
                    🔄 Refresh
                  </button>
                </div>
              </div>

              {loading ? (
                <div className="loading">Loading analytics data...</div>
              ) : (
                <>
                  {/* Financial Dashboard Summary */}
                  {analyticsData && (
                    <div className="analytics-grid">
                      <div className="analytics-card">
                        <div className="analytics-icon">💰</div>
                        <div className="analytics-info">
                          <span className="analytics-value">₹{(analyticsData.totalEarnings || 0).toLocaleString()}</span>
                          <span className="analytics-label">Total Earnings</span>
                        </div>
                      </div>
                      <div className="analytics-card">
                        <div className="analytics-icon">🏪</div>
                        <div className="analytics-info">
                          <span className="analytics-value">₹{(analyticsData.restaurantEarnings || 0).toLocaleString()}</span>
                          <span className="analytics-label">Restaurant Earnings</span>
                        </div>
                      </div>
                      <div className="analytics-card">
                        <div className="analytics-icon">🚴</div>
                        <div className="analytics-info">
                          <span className="analytics-value">₹{(analyticsData.deliveryEarnings || 0).toLocaleString()}</span>
                          <span className="analytics-label">Delivery Earnings</span>
                        </div>
                      </div>
                      <div className="analytics-card">
                        <div className="analytics-icon">🏛️</div>
                        <div className="analytics-info">
                          <span className="analytics-value">₹{(analyticsData.platformCommissions || 0).toLocaleString()}</span>
                          <span className="analytics-label">Platform Commissions</span>
                        </div>
                      </div>
                      <div className="analytics-card">
                        <div className="analytics-icon">📊</div>
                        <div className="analytics-info">
                          <span className="analytics-value">₹{(analyticsData.totalPayouts || 0).toLocaleString()}</span>
                          <span className="analytics-label">Total Payouts</span>
                        </div>
                      </div>
                      <div className="analytics-card">
                        <div className="analytics-icon">⏳</div>
                        <div className="analytics-info">
                          <span className="analytics-value">₹{(analyticsData.pendingPayouts || 0).toLocaleString()}</span>
                          <span className="analytics-label">Pending Payouts</span>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Payout Forecast */}
                  {payoutForecast && (
                    <div className="forecast-section">
                      <h4>🔮 Payout Forecast</h4>
                      <div className="forecast-cards">
                        <div className="forecast-card">
                          <h5>Next Restaurant Payout</h5>
                          <div className="forecast-value">₹{(payoutForecast.nextRestaurantPayout || 0).toLocaleString()}</div>
                          <div className="forecast-date">{payoutForecast.nextPayoutDate || 'Next Monday'}</div>
                          <div className="forecast-confidence">
                            Confidence: {Math.round((payoutForecast.restaurantConfidence || 0) * 100)}%
                          </div>
                        </div>
                        <div className="forecast-card">
                          <h5>Next Delivery Payout</h5>
                          <div className="forecast-value">₹{(payoutForecast.nextDeliveryPayout || 0).toLocaleString()}</div>
                          <div className="forecast-date">{payoutForecast.nextPayoutDate || 'Next Monday'}</div>
                          <div className="forecast-confidence">
                            Confidence: {Math.round((payoutForecast.deliveryConfidence || 0) * 100)}%
                          </div>
                        </div>
                        <div className="forecast-card total">
                          <h5>Total Weekly Forecast</h5>
                          <div className="forecast-value">₹{(payoutForecast.totalForecast || 0).toLocaleString()}</div>
                          <div className="forecast-partners">
                            {payoutForecast.restaurantCount || 0} restaurants, {payoutForecast.deliveryCount || 0} partners
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Trends */}
                  {(earningsTrend.length > 0 || payoutTrend.length > 0) && (
                    <div className="trends-section">
                      <h4>📊 Trends</h4>
                      <div className="trends-grid">
                        <div className="trend-card">
                          <h5>Earnings Trend</h5>
                          <div className="trend-data">
                            {earningsTrend.slice(-7).map((item, index) => (
                              <div key={index} className="trend-item">
                                <span className="trend-date">{item.date || item._id}</span>
                                <span className="trend-value">₹{(item.total || 0).toLocaleString()}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                        <div className="trend-card">
                          <h5>Payout Trend</h5>
                          <div className="trend-data">
                            {payoutTrend.slice(-7).map((item, index) => (
                              <div key={index} className="trend-item">
                                <span className="trend-date">{item.date || item._id}</span>
                                <span className="trend-value">₹{(item.total || 0).toLocaleString()}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* P2: Exports Tab */}
          {activeTab === 'exports' && (
            <div className="exports-content">
              <h3>📥 Export Financial Data</h3>
              <p className="exports-description">
                Download financial reports in CSV or JSON format for analysis and record-keeping.
              </p>

              <div className="export-cards">
                <div className="export-card">
                  <div className="export-icon">💰</div>
                  <h4>Platform Financials</h4>
                  <p>Complete platform financial summary including revenue, commissions, and payouts.</p>
                  <div className="export-buttons">
                    <button
                      className="btn btn-primary"
                      onClick={() => handleExport('platform-financials', { format: 'csv' })}
                      disabled={exportLoading}
                    >
                      📥 CSV
                    </button>
                    <button
                      className="btn btn-secondary"
                      onClick={() => handleExport('platform-financials', { format: 'json' })}
                      disabled={exportLoading}
                    >
                      📥 JSON
                    </button>
                  </div>
                </div>

                <div className="export-card">
                  <div className="export-icon">📊</div>
                  <h4>Payout History</h4>
                  <p>All payout transactions with status, dates, and amounts.</p>
                  <div className="export-buttons">
                    <button
                      className="btn btn-primary"
                      onClick={() => handleExport('payouts', { format: 'csv' })}
                      disabled={exportLoading}
                    >
                      📥 CSV
                    </button>
                    <button
                      className="btn btn-secondary"
                      onClick={() => handleExport('payouts', { format: 'json' })}
                      disabled={exportLoading}
                    >
                      📥 JSON
                    </button>
                  </div>
                </div>

                <div className="export-card">
                  <div className="export-icon">🧾</div>
                  <h4>TDS Report</h4>
                  <p>Tax Deducted at Source (TDS) report for compliance and filing.</p>
                  <div className="export-buttons">
                    <button
                      className="btn btn-primary"
                      onClick={() => handleExport('tds-report', { format: 'csv' })}
                      disabled={exportLoading}
                    >
                      📥 CSV
                    </button>
                    <button
                      className="btn btn-secondary"
                      onClick={() => handleExport('tds-report', { format: 'json' })}
                      disabled={exportLoading}
                    >
                      📥 JSON
                    </button>
                  </div>
                </div>

                <div className="export-card">
                  <div className="export-icon">📋</div>
                  <h4>GST Report</h4>
                  <p>GST collected and payable for tax filing and compliance.</p>
                  <div className="export-buttons">
                    <button
                      className="btn btn-primary"
                      onClick={() => handleExport('gst-report', { format: 'csv' })}
                      disabled={exportLoading}
                    >
                      📥 CSV
                    </button>
                    <button
                      className="btn btn-secondary"
                      onClick={() => handleExport('gst-report', { format: 'json' })}
                      disabled={exportLoading}
                    >
                      📥 JSON
                    </button>
                  </div>
                </div>
              </div>

              {exportLoading && (
                <div className="export-loading">
                  <div className="spinner"></div>
                  <p>Generating export file...</p>
                </div>
              )}
            </div>
          )}

          {/* Order Financials Tab */}
          {activeTab === 'orderFinancials' && (
            <div className="order-financials-content">
              <h3>🧾 Order Financial Breakdown</h3>
              <p className="tab-description">
                View detailed financial breakdown for each order - customer bill, restaurant earnings, delivery partner earnings, and platform fees.
              </p>

              {/* Search */}
              <form onSubmit={handleOrderFinancialsSearch} className="search-form">
                <input
                  type="text"
                  placeholder="Search by order number..."
                  value={orderFinancialsSearch}
                  onChange={(e) => setOrderFinancialsSearch(e.target.value)}
                  className="search-input"
                />
                <button type="submit" className="btn btn-primary">Search</button>
                {orderFinancialsSearch && (
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => {
                      setOrderFinancialsSearch('');
                      fetchOrderFinancials(1, '');
                    }}
                  >
                    Clear
                  </button>
                )}
              </form>

              {/* Summary Stats */}
              {orderFinancialsSummary && (
                <div className="financials-summary">
                  <div className="summary-card">
                    <span className="summary-label">Total Orders</span>
                    <span className="summary-value">{orderFinancialsSummary.orderCount}</span>
                  </div>
                  <div className="summary-card">
                    <span className="summary-label">Customer Paid</span>
                    <span className="summary-value">₹{orderFinancialsSummary.totalCustomerPaid?.toLocaleString()}</span>
                  </div>
                  <div className="summary-card restaurant">
                    <span className="summary-label">Restaurant Earnings</span>
                    <span className="summary-value">₹{orderFinancialsSummary.totalRestaurantEarnings?.toLocaleString()}</span>
                  </div>
                  <div className="summary-card delivery">
                    <span className="summary-label">Delivery Earnings</span>
                    <span className="summary-value">₹{orderFinancialsSummary.totalDeliveryEarnings?.toLocaleString()}</span>
                  </div>
                  <div className="summary-card platform">
                    <span className="summary-label">Platform Earnings</span>
                    <span className="summary-value">₹{orderFinancialsSummary.totalPlatformEarnings?.toLocaleString()}</span>
                  </div>
                  {orderFinancialsSummary.totalDiscounts > 0 && (
                    <div className="summary-card discount">
                      <span className="summary-label">Total Discounts ({orderFinancialsSummary.ordersWithDiscount} orders)</span>
                      <span className="summary-value negative">-₹{orderFinancialsSummary.totalDiscounts?.toLocaleString()}</span>
                    </div>
                  )}
                  {orderFinancialsSummary.totalRestaurantDiscounts > 0 && (
                    <div className="summary-card discount-detail">
                      <span className="summary-label">↳ Restaurant Funded</span>
                      <span className="summary-value">₹{orderFinancialsSummary.totalRestaurantDiscounts?.toLocaleString()}</span>
                    </div>
                  )}
                  {orderFinancialsSummary.totalPlatformDiscounts > 0 && (
                    <div className="summary-card discount-detail">
                      <span className="summary-label">↳ Platform Funded</span>
                      <span className="summary-value">₹{orderFinancialsSummary.totalPlatformDiscounts?.toLocaleString()}</span>
                    </div>
                  )}
                  <div className="summary-card tds">
                    <span className="summary-label">TDS Collected</span>
                    <span className="summary-value">₹{orderFinancialsSummary.totalTdsCollected?.toLocaleString()}</span>
                  </div>
                </div>
              )}

              {/* Orders List */}
              {orderFinancialsLoading ? (
                <div className="loading-spinner">Loading orders...</div>
              ) : orderFinancials.length === 0 ? (
                <div className="no-data">No orders found with calculated earnings.</div>
              ) : (
                <div className="orders-financials-list">
                  {orderFinancials.map((order) => (
                    <div key={order._id} className="order-financial-card">
                      {/* Order Header - Always visible */}
                      <div
                        className="order-financial-header"
                        onClick={() => setExpandedOrder(expandedOrder === order._id ? null : order._id)}
                      >
                        <div className="order-info">
                          <span className="order-number">{order.orderNumber}</span>
                          <span className="order-date">
                            {new Date(order.deliveredAt || order.createdAt).toLocaleDateString('en-IN', {
                              day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
                            })}
                          </span>
                        </div>
                        <div className="order-parties">
                          <span className="party restaurant">🏪 {order.restaurant?.name}</span>
                          {order.deliveryPartner && (
                            <span className="party delivery">🚴 {order.deliveryPartner?.name}</span>
                          )}
                        </div>
                        <div className="order-amounts">
                          <span className="customer-total">₹{order.customerBill?.total?.toLocaleString()}</span>
                          <span className="payment-method">{order.paymentMethod?.toUpperCase()}</span>
                        </div>
                        <span className="expand-icon">{expandedOrder === order._id ? '▲' : '▼'}</span>
                      </div>

                      {/* Expanded Details - Complete Step-by-Step Financial Breakdown */}
                      {expandedOrder === order._id && (
                        <div className="order-financial-details detailed-breakdown">
                          {/* STAGE 1: Customer Payment */}
                          <div className="stage-section">
                            <div className="stage-header-bar">
                              <span className="stage-badge">STAGE 1</span>
                              <h4>👤 Customer Places Order & Pays</h4>
                            </div>
                            <div className="stage-body">
                              <p className="stage-description">Customer Bill Breakdown (what customer pays):</p>
                              <table className="detailed-calc-table">
                                <tbody>
                                  <tr>
                                    <td>Food Subtotal</td>
                                    <td className="amount">₹{order.customerBill?.subtotal?.toFixed(2)}</td>
                                    <td className="note"></td>
                                  </tr>
                                  <tr>
                                    <td>Food GST (5%)</td>
                                    <td className="amount">₹{order.customerBill?.tax?.toFixed(2)}</td>
                                    <td className="note">
                                      {order.restaurantBreakdown?.section9_5Applied
                                        ? '→ Section 9(5): ECO pays directly to govt (NOT restaurant)'
                                        : '→ Collected for restaurant'}
                                    </td>
                                  </tr>
                                  <tr>
                                    <td>Platform Fee</td>
                                    <td className="amount">₹{order.customerBill?.platformFee?.toFixed(2)}</td>
                                    <td className="note"></td>
                                  </tr>
                                  <tr>
                                    <td>Platform Fee GST (18%)</td>
                                    <td className="amount">₹{order.platformBreakdown?.platformFeeGst?.toFixed(2)}</td>
                                    <td className="note">→ Platform's GST liability</td>
                                  </tr>
                                  <tr>
                                    <td>Delivery Fee</td>
                                    <td className="amount">₹{order.customerBill?.deliveryFee?.toFixed(2)}</td>
                                    <td className="note"></td>
                                  </tr>
                                  <tr>
                                    <td>Delivery Fee GST (18%)</td>
                                    <td className="amount">₹{order.customerBill?.deliveryFeeGst?.toFixed(2)}</td>
                                    <td className="note">→ Platform's GST liability</td>
                                  </tr>
                                  {order.customerBill?.packagingCharges > 0 && (
                                    <tr>
                                      <td>Packaging Charges</td>
                                      <td className="amount">₹{order.customerBill?.packagingCharges?.toFixed(2)}</td>
                                      <td className="note"></td>
                                    </tr>
                                  )}
                                  {order.customerBill?.discount > 0 && (
                                    <tr className="discount-row">
                                      <td>
                                        {order.couponApplied?.isFirstOrderFreeDelivery ? '🚀 Free Delivery' : 'Discount'}
                                        {order.couponApplied?.code && ` (${order.couponApplied.code})`}
                                      </td>
                                      <td className="amount negative">-₹{order.customerBill?.discount?.toFixed(2)}</td>
                                      <td className="note">→ {order.couponApplied?.isFirstOrderFreeDelivery ? 'First order benefit' : 'Applied to bill'}</td>
                                    </tr>
                                  )}
                                  <tr className="total-row">
                                    <td><strong>TOTAL CUSTOMER PAID</strong></td>
                                    <td className="amount"><strong>₹{order.customerBill?.total?.toFixed(2)}</strong></td>
                                    <td className="note"></td>
                                  </tr>
                                </tbody>
                              </table>
                              <div className="payment-info">
                                <span className="payment-badge">{order.paymentMethod?.toUpperCase()}</span>
                                <span className="order-time">{new Date(order.createdAt).toLocaleString('en-IN')}</span>
                              </div>
                            </div>
                          </div>

                          {/* STAGE 2: Earnings Calculated */}
                          <div className="stage-section">
                            <div className="stage-header-bar">
                              <span className="stage-badge">STAGE 2</span>
                              <h4>📊 Order Delivered → Earnings Calculated</h4>
                            </div>
                            <div className="stage-body">
                              <p className="stage-description">
                                When status changed to <code>'delivered'</code>, <code>calculateAndStoreEarnings()</code> ran with <strong>integer arithmetic (paisa)</strong>.
                              </p>

                              {/* Restaurant Earnings Calculation */}
                              <div className="earnings-calc-block restaurant">
                                <h5>🏪 Restaurant Earnings Calculation</h5>
                                {order.restaurantBreakdown?.section9_5Applied && (
                                  <div className="section95-banner" style={{
                                    background: '#FEF3C7',
                                    border: '1px solid #F59E0B',
                                    borderRadius: 8,
                                    padding: '10px 14px',
                                    marginBottom: 12,
                                    fontSize: 13,
                                    color: '#92400E'
                                  }}>
                                    <strong>⚖️ Section 9(5) CGST Act applied.</strong>{' '}
                                    FoodWinc (ECO) is the deemed supplier of restaurant services.
                                    Food GST of ₹{order.customerBill?.tax?.toFixed(2)} is COLLECTED
                                    from the customer but is the platform's liability — it goes
                                    to the government, NOT to the restaurant. The restaurant's
                                    gross is subtotal-only.
                                  </div>
                                )}
                                <div className="calc-steps">
                                  <div className="calc-step">
                                    <span className="step-label">Step 1: Convert to Paisa</span>
                                    <code>subtotalPaisa = {order.customerBill?.subtotal?.toFixed(2)} × 100 = {Math.round((order.customerBill?.subtotal || 0) * 100).toLocaleString()} paisa</code>
                                  </div>
                                  <div className="calc-step">
                                    <span className="step-label">
                                      Step 2: Food GST (5%){order.restaurantBreakdown?.section9_5Applied
                                        ? ' — Section 9(5): retained by platform for govt remittance'
                                        : ' — already collected from customer'}
                                    </span>
                                    <code>foodGstPaisa = {Math.round((order.customerBill?.subtotal || 0) * 100).toLocaleString()} × 5% = {Math.round((order.customerBill?.tax || 0) * 100).toLocaleString()} paisa (₹{order.customerBill?.tax?.toFixed(2)})</code>
                                  </div>
                                  <div className="calc-step">
                                    <span className="step-label">Step 3: Calculate Gross Amount</span>
                                    {order.restaurantBreakdown?.section9_5Applied ? (
                                      ((order.restaurantBreakdown?.grossAmount || 0) - (order.customerBill?.subtotal || 0)) > 0.005 ? (
                                        <code>restaurantGrossPaisa = subtotal ({Math.round((order.customerBill?.subtotal || 0) * 100).toLocaleString()}) + platform-funded item discount ({Math.round(((order.restaurantBreakdown?.grossAmount || 0) - (order.customerBill?.subtotal || 0)) * 100).toLocaleString()}, restaurant made whole) = {Math.round((order.restaurantBreakdown?.grossAmount || 0) * 100).toLocaleString()} paisa (₹{order.restaurantBreakdown?.grossAmount?.toFixed(2)}) — food GST EXCLUDED under Section 9(5)</code>
                                      ) : (
                                        <code>restaurantGrossPaisa = subtotalPaisa only = {Math.round((order.restaurantBreakdown?.grossAmount || 0) * 100).toLocaleString()} paisa (₹{order.restaurantBreakdown?.grossAmount?.toFixed(2)}) — food GST EXCLUDED under Section 9(5)</code>
                                      )
                                    ) : (
                                      <code>restaurantGrossPaisa = {Math.round((order.customerBill?.subtotal || 0) * 100).toLocaleString()} + {Math.round((order.customerBill?.tax || 0) * 100).toLocaleString()} = {Math.round((order.restaurantBreakdown?.grossAmount || 0) * 100).toLocaleString()} paisa (₹{order.restaurantBreakdown?.grossAmount?.toFixed(2)})</code>
                                    )}
                                  </div>
                                  <div className="calc-step">
                                    <span className="step-label">Step 4: Restaurant Commission ({order.restaurantBreakdown?.commissionRate || 0}% - {order.restaurantBreakdown?.commissionRate === 0 ? 'Zero Commission Model' : 'Platform Commission'})</span>
                                    <code>restaurantCommissionPaisa = {Math.round((order.customerBill?.subtotal || 0) * 100).toLocaleString()} × {order.restaurantBreakdown?.commissionRate || 0}% = {Math.round((order.restaurantBreakdown?.commissionAmount || 0) * 100).toLocaleString()} paisa</code>
                                  </div>
                                  <div className="calc-step">
                                    <span className="step-label">Step 5: Estimated TDS under Section 194-O ({order.restaurantBreakdown?.tdsRate || 0.1}%) — actual TDS deducted at payout level</span>
                                    <code>estimatedTdsPaisa = {Math.round((order.customerBill?.subtotal || 0) * 100).toLocaleString()} × {order.restaurantBreakdown?.tdsRate || 0.1}% = {Math.round((order.restaurantBreakdown?.tdsDeducted || 0) * 100).toLocaleString()} paisa (₹{order.restaurantBreakdown?.tdsDeducted?.toFixed(2)})</code>
                                  </div>
                                  <div className="calc-step">
                                    <span className="step-label">Step 6: Calculate Net Payable</span>
                                    <code>netPayablePaisa = {Math.round((order.restaurantBreakdown?.grossAmount || 0) * 100).toLocaleString()} - {Math.round((order.restaurantBreakdown?.tdsDeducted || 0) * 100).toLocaleString()} = {Math.round((order.restaurantBreakdown?.netPayable || 0) * 100).toLocaleString()} paisa (₹{order.restaurantBreakdown?.netPayable?.toFixed(2)})</code>
                                  </div>
                                </div>
                                <table className="earnings-summary-table">
                                  <tbody>
                                    <tr>
                                      <td>Gross Amount</td>
                                      <td>₹{order.restaurantBreakdown?.grossAmount?.toFixed(2)}</td>
                                      <td>
                                        {order.restaurantBreakdown?.section9_5Applied
                                          ? (((order.restaurantBreakdown?.grossAmount || 0) - (order.customerBill?.subtotal || 0)) > 0.005
                                              ? '(subtotal + platform-funded item discount — restaurant made whole; food GST is ECO liability)'
                                              : '(subtotal only — food GST is ECO liability)')
                                          : '(subtotal + GST collected)'}
                                      </td>
                                    </tr>
                                    {order.restaurantBreakdown?.section9_5Applied && (
                                      <tr className="section95-note">
                                        <td>↳ Food GST @ 5%</td>
                                        <td>₹{order.customerBill?.tax?.toFixed(2)}</td>
                                        <td>NOT paid to restaurant — platform's liability under Section 9(5)</td>
                                      </tr>
                                    )}
                                    <tr><td>Commission ({order.restaurantBreakdown?.commissionRate || 0}%)</td><td>₹{order.restaurantBreakdown?.commissionAmount?.toFixed(2)}</td><td>({order.restaurantBreakdown?.commissionRate === 0 ? 'zero commission model' : 'platform commission'})</td></tr>
                                    <tr className="deduction"><td>Estimated TDS ({order.restaurantBreakdown?.tdsRate || 0.1}%)</td><td>-₹{order.restaurantBreakdown?.tdsDeducted?.toFixed(2)}</td><td>(actual TDS deducted at payout level)</td></tr>
                                    {order.restaurantBreakdown?.discountAbsorbed > 0 && (
                                      <tr className="deduction"><td>Coupon Absorbed</td><td>-₹{order.restaurantBreakdown?.discountAbsorbed?.toFixed(2)}</td><td>({order.couponApplied?.code})</td></tr>
                                    )}
                                    <tr className="net-total"><td><strong>NET PAYABLE</strong></td><td><strong>₹{order.restaurantBreakdown?.netPayable?.toFixed(2)}</strong></td><td>→ Goes to restaurant balance</td></tr>
                                  </tbody>
                                </table>
                                <div className={`payout-status-badge ${order.restaurantBreakdown?.payoutStatus}`}>
                                  {order.restaurantBreakdown?.payoutStatus === 'paid' ? '✅ PAID' : '⏳ PENDING PAYOUT'}
                                </div>
                              </div>

                              {/* Delivery Partner Earnings Calculation */}
                              {order.deliveryBreakdown && (
                                <div className="earnings-calc-block delivery">
                                  <h5>🚴 Delivery Partner Earnings Calculation</h5>
                                  <div className="calc-steps">
                                    <div className="calc-step">
                                      <span className="step-label">Step 1: Convert to Paisa</span>
                                      <code>deliveryFeePaisa = {order.customerBill?.deliveryFee?.toFixed(2)} × 100 = {Math.round((order.customerBill?.deliveryFee || 0) * 100).toLocaleString()} paisa</code>
                                    </div>
                                    <div className="calc-step">
                                      <span className="step-label">Step 2: Platform Commission ({order.deliveryBreakdown?.commissionRate || 10}%)</span>
                                      <code>dpCommissionPaisa = {Math.round((order.customerBill?.deliveryFee || 0) * 100).toLocaleString()} × {order.deliveryBreakdown?.commissionRate || 10}% = {Math.round((order.deliveryBreakdown?.commissionAmount || 0) * 100).toLocaleString()} paisa (₹{order.deliveryBreakdown?.commissionAmount?.toFixed(2)})</code>
                                    </div>
                                    <div className="calc-step">
                                      <span className="step-label">Step 3: Calculate Net Delivery Fee</span>
                                      <code>dpNetPaisa = {Math.round((order.customerBill?.deliveryFee || 0) * 100).toLocaleString()} - {Math.round((order.deliveryBreakdown?.commissionAmount || 0) * 100).toLocaleString()} = {Math.round((order.deliveryBreakdown?.netAmount || 0) * 100).toLocaleString()} paisa (₹{order.deliveryBreakdown?.netAmount?.toFixed(2)})</code>
                                    </div>
                                    {(order.tip?.amount > 0 || order.deliveryBreakdown?.surgeBonus > 0 || order.deliveryBreakdown?.slotIncentive > 0) && (
                                      <div className="calc-step">
                                        <span className="step-label">Step 4: Add Tips & Incentives</span>
                                        <code>
                                          tipsPaisa = {order.tip?.amount ? `${Math.round(order.tip.amount * 100).toLocaleString()} paisa (₹${order.tip.amount.toFixed(2)})` : '0'}
                                          {order.deliveryBreakdown?.surgeBonus > 0 && ` | surgePaisa = ${Math.round(order.deliveryBreakdown.surgeBonus * 100).toLocaleString()} paisa`}
                                          {order.deliveryBreakdown?.slotIncentive > 0 && ` | incentivePaisa = ${Math.round(order.deliveryBreakdown.slotIncentive * 100).toLocaleString()} paisa`}
                                        </code>
                                      </div>
                                    )}
                                  </div>
                                  <table className="earnings-summary-table">
                                    <tbody>
                                      <tr><td>Gross Delivery Fee</td><td>₹{order.deliveryBreakdown?.grossAmount?.toFixed(2)}</td><td></td></tr>
                                      <tr className="deduction"><td>Platform Commission</td><td>-₹{order.deliveryBreakdown?.commissionAmount?.toFixed(2)}</td><td>({order.deliveryBreakdown?.commissionRate || 10}% of delivery fee)</td></tr>
                                      {order.deliveryBreakdown?.surgeBonus > 0 && (
                                        <tr className="bonus"><td>Surge Bonus</td><td>+₹{order.deliveryBreakdown?.surgeBonus?.toFixed(2)}</td><td></td></tr>
                                      )}
                                      {order.deliveryBreakdown?.slotIncentive > 0 && (
                                        <tr className="bonus"><td>Slot Incentive</td><td>+₹{order.deliveryBreakdown?.slotIncentive?.toFixed(2)}</td><td></td></tr>
                                      )}
                                      {order.tip?.amount > 0 && (
                                        <tr className="bonus"><td>Tip</td><td>+₹{order.tip?.amount?.toFixed(2)}</td><td></td></tr>
                                      )}
                                      <tr className="net-total"><td><strong>NET EARNINGS</strong></td><td><strong>₹{order.deliveryBreakdown?.netAmount?.toFixed(2)}</strong></td><td>→ Goes to delivery partner balance</td></tr>
                                    </tbody>
                                  </table>
                                  <div className={`payout-status-badge ${order.deliveryBreakdown?.payoutStatus}`}>
                                    {order.deliveryBreakdown?.payoutStatus === 'paid' ? '✅ PAID' : '⏳ PENDING PAYOUT'}
                                  </div>
                                </div>
                              )}

                              {/* Platform Earnings Calculation */}
                              <div className="earnings-calc-block platform">
                                <h5>💼 Platform Earnings Calculation</h5>
                                <div className="calc-steps">
                                  <div className="calc-step">
                                    <span className="step-label">Step 1: Platform Fee Revenue</span>
                                    <code>platformFeePaisa = {order.customerBill?.platformFee?.toFixed(2)} × 100 = {Math.round((order.customerBill?.platformFee || 0) * 100).toLocaleString()} paisa</code>
                                  </div>
                                  <div className="calc-step">
                                    <span className="step-label">Step 2: Platform Fee GST (18% - platform's liability)</span>
                                    <code>platformFeeGstPaisa = {Math.round((order.customerBill?.platformFee || 0) * 100).toLocaleString()} × 18% = {Math.round((order.platformBreakdown?.platformFeeGst || 0) * 100).toLocaleString()} paisa (₹{order.platformBreakdown?.platformFeeGst?.toFixed(2)})</code>
                                  </div>
                                  <div className="calc-step">
                                    <span className="step-label">Step 2b: Delivery Fee GST (18% - platform's liability)</span>
                                    <code>deliveryFeeGstPaisa = {Math.round((order.customerBill?.deliveryFee || 0) * 100).toLocaleString()} × 18% = {Math.round((order.platformBreakdown?.deliveryFeeGst || 0) * 100).toLocaleString()} paisa (₹{order.platformBreakdown?.deliveryFeeGst?.toFixed(2)})</code>
                                  </div>
                                  <div className="calc-step">
                                    <span className="step-label">Step 3: Commission from Delivery Fee</span>
                                    <code>deliveryCommissionPaisa = {Math.round((order.platformBreakdown?.deliveryCommission || 0) * 100).toLocaleString()} paisa (₹{order.platformBreakdown?.deliveryCommission?.toFixed(2)})</code>
                                  </div>
                                  <div className="calc-step">
                                    <span className="step-label">Step 4: Commission from Restaurant ({order.restaurantBreakdown?.commissionRate || 0}%)</span>
                                    <code>restaurantCommissionPaisa = {Math.round((order.platformBreakdown?.restaurantCommission || 0) * 100).toLocaleString()} paisa</code>
                                  </div>
                                  <div className="calc-step">
                                    <span className="step-label">Step 5: Calculate Total Platform Earnings</span>
                                    <code>totalEarningsPaisa = {Math.round((order.customerBill?.platformFee || 0) * 100).toLocaleString()} + {Math.round((order.platformBreakdown?.restaurantCommission || 0) * 100).toLocaleString()} + {Math.round((order.platformBreakdown?.deliveryCommission || 0) * 100).toLocaleString()} = {Math.round((order.platformBreakdown?.totalEarnings || order.platformBreakdown?.netEarnings || 0) * 100).toLocaleString()} paisa (₹{(order.platformBreakdown?.totalEarnings || order.platformBreakdown?.netEarnings)?.toFixed(2)})</code>
                                  </div>
                                </div>
                                <table className="earnings-summary-table">
                                  <tbody>
                                    <tr><td>Platform Fee</td><td>₹{order.platformBreakdown?.platformFee?.toFixed(2)}</td><td></td></tr>
                                    <tr><td>Platform Fee GST</td><td>₹{order.platformBreakdown?.platformFeeGst?.toFixed(2)}</td><td>(platform collects & remits to govt)</td></tr>
                                    <tr><td>Delivery Fee GST</td><td>₹{order.platformBreakdown?.deliveryFeeGst?.toFixed(2)}</td><td>(platform collects & remits to govt)</td></tr>
                                    <tr><td>Restaurant Commission</td><td>₹{order.platformBreakdown?.restaurantCommission?.toFixed(2)}</td><td>({order.restaurantBreakdown?.commissionRate === 0 ? 'zero commission model' : `${order.restaurantBreakdown?.commissionRate}%`})</td></tr>
                                    <tr><td>Delivery Commission</td><td>₹{order.platformBreakdown?.deliveryCommission?.toFixed(2)}</td><td>({order.deliveryBreakdown?.commissionRate || 10}% of delivery fee)</td></tr>
                                    <tr><td>TDS Collected</td><td>₹{order.platformBreakdown?.tdsCollected?.toFixed(2)}</td><td>(collected from restaurant, remit to govt)</td></tr>
                                    {order.platformBreakdown?.discountAbsorbed > 0 && (
                                      <tr className="deduction">
                                        <td>{order.couponApplied?.isFirstOrderFreeDelivery ? '🚀 Free Delivery Absorbed' : 'Discount Absorbed'}</td>
                                        <td>-₹{order.platformBreakdown?.discountAbsorbed?.toFixed(2)}</td>
                                        <td>({order.couponApplied?.code}{order.couponApplied?.appliesTo === 'delivery' ? ' - delivery' : ''})</td>
                                      </tr>
                                    )}
                                    <tr className="net-total"><td><strong>TOTAL EARNINGS</strong></td><td><strong className={order.platformBreakdown?.netEarnings < 0 ? 'negative' : ''}>₹{(order.platformBreakdown?.netEarnings || order.platformBreakdown?.totalEarnings)?.toFixed(2)}</strong></td><td></td></tr>
                                    <tr className="liability-row"><td>GST LIABILITY (Platform Fee)</td><td>₹{order.platformBreakdown?.platformFeeGst?.toFixed(2)}</td><td>(18% on platform fee — to remit)</td></tr>
                                    <tr className="liability-row"><td>GST LIABILITY (Delivery Fee)</td><td>₹{order.platformBreakdown?.deliveryFeeGst?.toFixed(2)}</td><td>(18% on delivery fee — to remit)</td></tr>
                                    {order.platformBreakdown?.section9_5Applied && order.platformBreakdown?.section9_5GstPayable > 0 && (
                                      <tr className="liability-row section95">
                                        <td>GST LIABILITY (Section 9(5) — Food)</td>
                                        <td>₹{order.platformBreakdown?.section9_5GstPayable?.toFixed(2)}</td>
                                        <td>(food GST collected from customer — ECO remits directly to govt under Section 9(5))</td>
                                      </tr>
                                    )}
                                    <tr className="liability-row"><td>TDS LIABILITY</td><td>₹{order.platformBreakdown?.tdsCollected?.toFixed(2)}</td><td>(to remit to Income Tax Dept)</td></tr>
                                  </tbody>
                                </table>
                                {order.platformBreakdown?.section9_5Applied && (
                                  <div className="section95-footer" style={{
                                    marginTop: 10,
                                    padding: '10px 14px',
                                    background: '#ECFDF5',
                                    border: '1px solid #10B981',
                                    borderRadius: 8,
                                    fontSize: 12,
                                    color: '#065F46'
                                  }}>
                                    ℹ️ <strong>Section 9(5) in effect:</strong> the food GST of{' '}
                                    ₹{order.customerBill?.tax?.toFixed(2)} is collected from the customer
                                    and held by FoodWinc in trust for the government. It appears on the
                                    customer bill but is NOT part of the restaurant's earnings. The platform's
                                    actual net cash is reduced by this amount when it's remitted via GSTR-3B
                                    (Table 3.1.1(i)).
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>

                          {/* STAGE 3: Storage Confirmation */}
                          <div className="stage-section storage">
                            <div className="stage-header-bar">
                              <span className="stage-badge">STAGE 3</span>
                              <h4>💾 Earnings Stored in Order Document</h4>
                            </div>
                            <div className="stage-body">
                              <p className="stage-description">
                                Earnings stored atomically at{' '}
                                <code>{order.earnings?.restaurantEarnings?.calculatedAt ? new Date(order.earnings.restaurantEarnings.calculatedAt).toLocaleString('en-IN') : new Date(order.deliveredAt || order.updatedAt).toLocaleString('en-IN')}</code>
                              </p>
                              {order.restaurantBreakdown?.section9_5Applied && (
                                <p style={{
                                  fontSize: 12,
                                  color: '#92400E',
                                  background: '#FEF3C7',
                                  padding: '6px 10px',
                                  borderRadius: 6,
                                  marginBottom: 10
                                }}>
                                  ⚖️ Section 9(5) order — <code>restaurantEarnings.gstCollected</code> is stored as{' '}
                                  <strong>0</strong> because the ECO (platform) is the deemed supplier and holds
                                  the food GST directly. The <code>0.05</code> value you see on the customer bill
                                  is the ₹ paid by the customer, but it does NOT flow to the restaurant.
                                </p>
                              )}
                              <div className="stored-data-preview">
                                {/*
                                  This block now mirrors the REAL stored document
                                  (`order.earnings.restaurantEarnings.*`) via the backend
                                  breakdown fields. Previously it fabricated
                                  `gstCollected: customerBill.tax` which was misleading
                                  for Section 9(5) orders where the stored value is 0
                                  but the customer bill tax is non-zero.
                                */}
                                <pre>{JSON.stringify({
                                  restaurantEarnings: {
                                    grossAmount: order.restaurantBreakdown?.grossAmount,
                                    gstCollected: order.restaurantBreakdown?.gstCollected || 0,
                                    gstLiability: order.restaurantBreakdown?.gstLiability || 0,
                                    gstLiabilityOwner: order.restaurantBreakdown?.gstLiabilityOwner || 'restaurant',
                                    section9_5Applied: order.restaurantBreakdown?.section9_5Applied || false,
                                    tdsRate: order.restaurantBreakdown?.tdsRate || 0.1,
                                    tdsDeducted: order.restaurantBreakdown?.tdsDeducted,
                                    netPayable: order.restaurantBreakdown?.netPayable,
                                    payoutStatus: order.restaurantBreakdown?.payoutStatus
                                  },
                                  deliveryEarnings: order.deliveryBreakdown ? {
                                    grossAmount: order.deliveryBreakdown?.grossAmount,
                                    commissionRate: order.deliveryBreakdown?.commissionRate || 10,
                                    commissionAmount: order.deliveryBreakdown?.commissionAmount,
                                    netAmount: order.deliveryBreakdown?.netAmount,
                                    payoutStatus: order.deliveryBreakdown?.payoutStatus
                                  } : null,
                                  platformEarnings: {
                                    platformFee: order.platformBreakdown?.platformFee,
                                    platformFeeGst: order.platformBreakdown?.platformFeeGst,
                                    deliveryFeeGst: order.platformBreakdown?.deliveryFeeGst,
                                    deliveryCommission: order.platformBreakdown?.deliveryCommission,
                                    ...(order.platformBreakdown?.section9_5Applied ? {
                                      section9_5Applied: true,
                                      section9_5GstCollected: order.platformBreakdown?.section9_5GstCollected || 0,
                                      section9_5GstPayable: order.platformBreakdown?.section9_5GstPayable || 0
                                    } : {}),
                                    tdsCollected: order.platformBreakdown?.tdsCollected,
                                    totalEarnings: order.platformBreakdown?.netEarnings
                                  }
                                }, null, 2)}</pre>
                              </div>
                            </div>
                          </div>

                          {/* Visual Flow Summary */}
                          <div className="order-flow-visual">
                            <div className="flow-item customer">
                              <div className="flow-label">👤 CUSTOMER PAID</div>
                              <div className="flow-amount">₹{order.customerBill?.total?.toFixed(2)}</div>
                            </div>
                            <div className="flow-arrow">→</div>
                            <div className="flow-distribution">
                              <div className="flow-item restaurant">
                                <div className="flow-label">🏪 Restaurant</div>
                                <div className="flow-amount">₹{order.restaurantBreakdown?.netPayable?.toFixed(2)}</div>
                              </div>
                              <div className="flow-item delivery">
                                <div className="flow-label">🚴 Delivery</div>
                                <div className="flow-amount">₹{order.deliveryBreakdown?.netAmount?.toFixed(2) || '0.00'}</div>
                              </div>
                              <div className="flow-item platform">
                                <div className="flow-label">💼 Platform</div>
                                <div className="flow-amount">₹{(order.platformBreakdown?.netEarnings || order.platformBreakdown?.totalEarnings)?.toFixed(2)}</div>
                              </div>
                            </div>
                          </div>

                          {/* Surge Info */}
                          {order.surgeApplied && (
                            <div className="surge-info-banner">
                              ⚡ Surge Applied: {order.surgeMultiplier}x multiplier (+₹{order.surgeAmount?.toFixed(2)} to delivery partner)
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Pagination */}
              {orderFinancialsPagination && orderFinancialsPagination.pages > 1 && (
                <div className="pagination">
                  <button
                    onClick={() => handleOrderFinancialsPageChange(orderFinancialsPage - 1)}
                    disabled={orderFinancialsPage <= 1}
                    className="btn btn-secondary"
                  >
                    ← Previous
                  </button>
                  <span className="page-info">
                    Page {orderFinancialsPage} of {orderFinancialsPagination.pages}
                    ({orderFinancialsPagination.total} orders)
                  </span>
                  <button
                    onClick={() => handleOrderFinancialsPageChange(orderFinancialsPage + 1)}
                    disabled={orderFinancialsPage >= orderFinancialsPagination.pages}
                    className="btn btn-secondary"
                  >
                    Next →
                  </button>
                </div>
              )}
            </div>
          )}

          {/* ==========================================
              OPERATIONS TAB - P2 Features
              ========================================== */}
          {activeTab === 'operations' && (
            <div className="operations-content">
              {/* Operations Sub-tabs */}
              <div className="operations-subtabs">
                <button
                  className={`subtab ${operationsSubTab === 'failedPayouts' ? 'active' : ''}`}
                  onClick={() => setOperationsSubTab('failedPayouts')}
                >
                  ❌ Failed Payouts
                </button>
                <button
                  className={`subtab ${operationsSubTab === 'circuitBreaker' ? 'active' : ''}`}
                  onClick={() => setOperationsSubTab('circuitBreaker')}
                >
                  ⚡ Circuit Breaker
                </button>
                <button
                  className={`subtab ${operationsSubTab === 'webhookQueue' ? 'active' : ''}`}
                  onClick={() => setOperationsSubTab('webhookQueue')}
                >
                  📨 Webhook Queue
                </button>
                <button
                  className={`subtab ${operationsSubTab === 'reconciliation' ? 'active' : ''}`}
                  onClick={() => setOperationsSubTab('reconciliation')}
                >
                  🔄 Reconciliation
                </button>
              </div>

              {operationsLoading ? (
                <div className="loading-spinner">Loading operations data...</div>
              ) : (
                <>
                  {/* ========== FAILED PAYOUTS SECTION ========== */}
                  {operationsSubTab === 'failedPayouts' && (
                    <div className="failed-payouts-section">
                      <div className="section-header">
                        <h3>❌ Failed Payouts Management</h3>
                        <div className="header-actions">
                          <button
                            className="btn btn-primary"
                            onClick={handleBulkRetry}
                            disabled={selectedFailedPayouts.length === 0}
                          >
                            🔄 Retry Selected ({selectedFailedPayouts.length})
                          </button>
                          <button
                            className="btn btn-secondary"
                            onClick={() => fetchFailedPayouts()}
                          >
                            ↻ Refresh
                          </button>
                        </div>
                      </div>

                      {/* Retry Statistics */}
                      {retryStats && (
                        <div className="retry-stats-grid">
                          <div className="stat-card failed">
                            <span className="stat-value">{retryStats.totalFailed || 0}</span>
                            <span className="stat-label">Total Failed</span>
                          </div>
                          <div className="stat-card pending">
                            <span className="stat-value">{retryStats.pendingRetry || 0}</span>
                            <span className="stat-label">Pending Retry</span>
                          </div>
                          <div className="stat-card retrying">
                            <span className="stat-value">{retryStats.currentlyRetrying || 0}</span>
                            <span className="stat-label">Currently Retrying</span>
                          </div>
                          <div className="stat-card success">
                            <span className="stat-value">{retryStats.successfulRetries || 0}</span>
                            <span className="stat-label">Successful Retries</span>
                          </div>
                          <div className="stat-card exhausted">
                            <span className="stat-value">{retryStats.exhaustedRetries || 0}</span>
                            <span className="stat-label">Max Retries Exhausted</span>
                          </div>
                        </div>
                      )}

                      {/* Failed Payouts List */}
                      <div className="failed-payouts-list">
                        {failedPayouts.length === 0 ? (
                          <div className="no-data">✅ No failed payouts! All payouts processed successfully.</div>
                        ) : (
                          <>
                            <div className="list-header">
                              <label className="checkbox-label">
                                <input
                                  type="checkbox"
                                  onChange={(e) => selectAllFailedPayouts(e.target.checked)}
                                  checked={failedPayouts.length > 0 && selectedFailedPayouts.length === failedPayouts.length}
                                />
                                Select All
                              </label>
                            </div>
                            <table className="data-table">
                              <thead>
                                <tr>
                                  <th></th>
                                  <th>Payout ID</th>
                                  <th>Recipient</th>
                                  <th>Amount</th>
                                  <th>Error</th>
                                  <th>Attempts</th>
                                  <th>Last Attempt</th>
                                  <th>Status</th>
                                </tr>
                              </thead>
                              <tbody>
                                {failedPayouts.map((payout) => (
                                  <tr key={payout._id}>
                                    <td>
                                      <input
                                        type="checkbox"
                                        checked={selectedFailedPayouts.includes(payout._id)}
                                        onChange={() => toggleFailedPayoutSelection(payout._id)}
                                      />
                                    </td>
                                    <td className="payout-id">{payout.payoutId || payout._id?.slice(-8)}</td>
                                    <td>
                                      <div className="recipient-cell">
                                        <span className="recipient-type">
                                          {payout.recipientType === 'restaurant' ? '🏪' : '🚴'}
                                        </span>
                                        <span className="recipient-name">{payout.recipientName || 'N/A'}</span>
                                      </div>
                                    </td>
                                    <td className="amount">₹{payout.amount?.toLocaleString()}</td>
                                    <td className="error-cell">
                                      <span className="error-message" title={payout.errorMessage}>
                                        {payout.errorMessage?.slice(0, 50)}...
                                      </span>
                                    </td>
                                    <td className="attempts">
                                      {payout.retryCount || 0} / {payout.maxRetries || 3}
                                    </td>
                                    <td className="last-attempt">
                                      {payout.lastAttemptAt
                                        ? new Date(payout.lastAttemptAt).toLocaleString('en-IN')
                                        : 'N/A'}
                                    </td>
                                    <td>
                                      <span className={`status-badge ${payout.status}`}>
                                        {payout.status}
                                      </span>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </>
                        )}
                      </div>

                      {/* Pagination for failed payouts */}
                      {failedPayoutsPagination && failedPayoutsPagination.pages > 1 && (
                        <div className="pagination">
                          <button
                            onClick={() => fetchFailedPayouts(failedPayoutsPagination.page - 1)}
                            disabled={failedPayoutsPagination.page <= 1}
                            className="btn btn-secondary"
                          >
                            ← Previous
                          </button>
                          <span className="page-info">
                            Page {failedPayoutsPagination.page} of {failedPayoutsPagination.pages}
                          </span>
                          <button
                            onClick={() => fetchFailedPayouts(failedPayoutsPagination.page + 1)}
                            disabled={failedPayoutsPagination.page >= failedPayoutsPagination.pages}
                            className="btn btn-secondary"
                          >
                            Next →
                          </button>
                        </div>
                      )}
                    </div>
                  )}

                  {/* ========== CIRCUIT BREAKER SECTION ========== */}
                  {operationsSubTab === 'circuitBreaker' && (
                    <div className="circuit-breaker-section">
                      <div className="section-header">
                        <h3>⚡ Circuit Breaker Status</h3>
                        <button
                          className="btn btn-secondary"
                          onClick={fetchOperationsData}
                        >
                          ↻ Refresh
                        </button>
                      </div>

                      {circuitBreakerStatus ? (
                        <div className="circuit-breaker-dashboard">
                          <div className={`circuit-status-card ${circuitBreakerStatus.state}`}>
                            <div className="status-indicator">
                              <span className={`status-light ${circuitBreakerStatus.state}`}></span>
                              <span className="status-text">
                                {circuitBreakerStatus.state === 'closed' && '✅ CLOSED (Healthy)'}
                                {circuitBreakerStatus.state === 'open' && '🔴 OPEN (Blocking Requests)'}
                                {circuitBreakerStatus.state === 'half-open' && '🟡 HALF-OPEN (Testing)'}
                              </span>
                            </div>
                            <p className="status-description">
                              {circuitBreakerStatus.state === 'closed' && 'Circuit is healthy. All payout requests are being processed normally.'}
                              {circuitBreakerStatus.state === 'open' && 'Circuit is open due to failures. Payout requests are being blocked to prevent cascading failures.'}
                              {circuitBreakerStatus.state === 'half-open' && 'Circuit is testing. A limited number of requests are being allowed through.'}
                            </p>
                          </div>

                          <div className="circuit-stats-grid">
                            <div className="stat-card">
                              <span className="stat-value">{circuitBreakerStatus.failureCount || 0}</span>
                              <span className="stat-label">Failure Count</span>
                            </div>
                            <div className="stat-card">
                              <span className="stat-value">{circuitBreakerStatus.successCount || 0}</span>
                              <span className="stat-label">Success Count</span>
                            </div>
                            <div className="stat-card">
                              <span className="stat-value">{circuitBreakerStatus.failureThreshold || 5}</span>
                              <span className="stat-label">Failure Threshold</span>
                            </div>
                            <div className="stat-card">
                              <span className="stat-value">{circuitBreakerStatus.resetTimeout || 30}s</span>
                              <span className="stat-label">Reset Timeout</span>
                            </div>
                          </div>

                          {circuitBreakerStatus.lastFailure && (
                            <div className="last-failure-info">
                              <h4>Last Failure</h4>
                              <p><strong>Time:</strong> {new Date(circuitBreakerStatus.lastFailure.timestamp).toLocaleString('en-IN')}</p>
                              <p><strong>Error:</strong> {circuitBreakerStatus.lastFailure.error || 'N/A'}</p>
                            </div>
                          )}

                          {circuitBreakerStatus.state !== 'closed' && (
                            <div className="circuit-actions">
                              <button
                                className="btn btn-warning"
                                onClick={handleResetCircuitBreaker}
                              >
                                🔄 Reset Circuit Breaker
                              </button>
                              <p className="action-warning">
                                ⚠️ Resetting the circuit breaker will allow payout requests to proceed.
                                Only reset if you've resolved the underlying issue.
                              </p>
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="no-data">Unable to fetch circuit breaker status.</div>
                      )}
                    </div>
                  )}

                  {/* ========== WEBHOOK QUEUE SECTION ========== */}
                  {operationsSubTab === 'webhookQueue' && (
                    <div className="webhook-queue-section">
                      <div className="section-header">
                        <h3>📨 Webhook Queue Management</h3>
                        <div className="header-actions">
                          <button
                            className="btn btn-primary"
                            onClick={handleProcessWebhookQueue}
                          >
                            ▶️ Process Queue
                          </button>
                          <button
                            className="btn btn-secondary"
                            onClick={() => { fetchPendingWebhooks(); fetchDeadLetterQueue(); }}
                          >
                            ↻ Refresh
                          </button>
                        </div>
                      </div>

                      {/* Webhook Queue Stats */}
                      {webhookQueueStats && (
                        <div className="webhook-stats-grid">
                          <div className="stat-card pending">
                            <span className="stat-value">{webhookQueueStats.pending || 0}</span>
                            <span className="stat-label">Pending</span>
                          </div>
                          <div className="stat-card processing">
                            <span className="stat-value">{webhookQueueStats.processing || 0}</span>
                            <span className="stat-label">Processing</span>
                          </div>
                          <div className="stat-card completed">
                            <span className="stat-value">{webhookQueueStats.completed || 0}</span>
                            <span className="stat-label">Completed</span>
                          </div>
                          <div className="stat-card failed">
                            <span className="stat-value">{webhookQueueStats.failed || 0}</span>
                            <span className="stat-label">Failed</span>
                          </div>
                          <div className="stat-card dead-letter">
                            <span className="stat-value">{webhookQueueStats.deadLetter || 0}</span>
                            <span className="stat-label">Dead Letter</span>
                          </div>
                        </div>
                      )}

                      {/* Pending Webhooks */}
                      <div className="webhook-list-section">
                        <h4>📋 Pending Webhooks</h4>
                        {pendingWebhooks.length === 0 ? (
                          <div className="no-data">No pending webhooks in queue.</div>
                        ) : (
                          <table className="data-table">
                            <thead>
                              <tr>
                                <th>Queue ID</th>
                                <th>Transfer ID</th>
                                <th>Event</th>
                                <th>Created</th>
                                <th>Attempts</th>
                                <th>Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              {pendingWebhooks.map((webhook) => (
                                <tr key={webhook._id}>
                                  <td className="queue-id">{webhook._id?.slice(-8)}</td>
                                  <td>{webhook.transferId || 'N/A'}</td>
                                  <td>{webhook.event || webhook.eventType || 'N/A'}</td>
                                  <td>{new Date(webhook.createdAt).toLocaleString('en-IN')}</td>
                                  <td>{webhook.attempts || 0}</td>
                                  <td>
                                    <button
                                      className="btn btn-sm btn-primary"
                                      onClick={() => handleRetryWebhook(webhook._id)}
                                    >
                                      Retry
                                    </button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        )}
                      </div>

                      {/* Dead Letter Queue */}
                      <div className="webhook-list-section dead-letter">
                        <h4>💀 Dead Letter Queue</h4>
                        {deadLetterQueue.length === 0 ? (
                          <div className="no-data">No items in dead letter queue.</div>
                        ) : (
                          <>
                            <table className="data-table">
                              <thead>
                                <tr>
                                  <th>Queue ID</th>
                                  <th>Transfer ID</th>
                                  <th>Error</th>
                                  <th>Failed At</th>
                                  <th>Attempts</th>
                                  <th>Actions</th>
                                </tr>
                              </thead>
                              <tbody>
                                {deadLetterQueue.map((item) => (
                                  <tr key={item._id}>
                                    <td className="queue-id">{item._id?.slice(-8)}</td>
                                    <td>{item.transferId || 'N/A'}</td>
                                    <td className="error-cell">
                                      <span className="error-message" title={item.lastError}>
                                        {item.lastError?.slice(0, 40)}...
                                      </span>
                                    </td>
                                    <td>{new Date(item.failedAt || item.updatedAt).toLocaleString('en-IN')}</td>
                                    <td>{item.attempts || 0}</td>
                                    <td>
                                      <button
                                        className="btn btn-sm btn-primary"
                                        onClick={() => handleRetryWebhook(item._id)}
                                      >
                                        Retry
                                      </button>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                            <div className="dead-letter-actions">
                              <button
                                className="btn btn-danger"
                                onClick={() => handleCleanupWebhookQueue(30)}
                              >
                                🗑️ Cleanup Items Older Than 30 Days
                              </button>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  )}

                  {/* ========== RECONCILIATION SECTION ========== */}
                  {operationsSubTab === 'reconciliation' && (
                    <div className="reconciliation-section">
                      <div className="section-header">
                        <h3>🔄 Reconciliation Management</h3>
                        <div className="header-actions">
                          <button
                            className="btn btn-primary"
                            onClick={() => handleRunReconciliation()}
                          >
                            ▶️ Run Reconciliation
                          </button>
                          <button
                            className="btn btn-secondary"
                            onClick={() => { fetchOperationsData(); fetchReconciliationReports(); }}
                          >
                            ↻ Refresh
                          </button>
                        </div>
                      </div>

                      {/* Reconciliation Status */}
                      {reconciliationStatus && (
                        <div className="reconciliation-status-card">
                          <div className="status-header">
                            <span className={`status-badge ${reconciliationStatus.status}`}>
                              {reconciliationStatus.status === 'healthy' && '✅ Healthy'}
                              {reconciliationStatus.status === 'warning' && '⚠️ Warning'}
                              {reconciliationStatus.status === 'critical' && '🔴 Critical'}
                              {reconciliationStatus.status === 'running' && '🔄 Running'}
                            </span>
                            <span className="last-run">
                              Last Run: {reconciliationStatus.lastRunAt
                                ? new Date(reconciliationStatus.lastRunAt).toLocaleString('en-IN')
                                : 'Never'}
                            </span>
                          </div>

                          <div className="reconciliation-stats">
                            <div className="stat-item">
                              <span className="label">Total Payouts Checked:</span>
                              <span className="value">{reconciliationStatus.totalChecked || 0}</span>
                            </div>
                            <div className="stat-item">
                              <span className="label">Matched:</span>
                              <span className="value success">{reconciliationStatus.matched || 0}</span>
                            </div>
                            <div className="stat-item">
                              <span className="label">Discrepancies:</span>
                              <span className="value error">{reconciliationStatus.discrepancies || 0}</span>
                            </div>
                            <div className="stat-item">
                              <span className="label">Missing in Cashfree:</span>
                              <span className="value warning">{reconciliationStatus.missingInCashfree || 0}</span>
                            </div>
                            <div className="stat-item">
                              <span className="label">Missing in DB:</span>
                              <span className="value warning">{reconciliationStatus.missingInDb || 0}</span>
                            </div>
                          </div>

                          {reconciliationStatus.discrepancies > 0 && (
                            <div className="discrepancy-alert">
                              <p>⚠️ {reconciliationStatus.discrepancies} discrepancies found in the last reconciliation.
                                Please review the reports below for details.</p>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Reconciliation Reports */}
                      <div className="reconciliation-reports">
                        <h4>📊 Historical Reports</h4>
                        {reconciliationReports.length === 0 ? (
                          <div className="no-data">No reconciliation reports available. Run a reconciliation to generate reports.</div>
                        ) : (
                          <table className="data-table">
                            <thead>
                              <tr>
                                <th>Report Date</th>
                                <th>Status</th>
                                <th>Total Checked</th>
                                <th>Matched</th>
                                <th>Discrepancies</th>
                                <th>Duration</th>
                              </tr>
                            </thead>
                            <tbody>
                              {reconciliationReports.map((report) => (
                                <tr key={report._id}>
                                  <td>{new Date(report.createdAt).toLocaleDateString('en-IN')}</td>
                                  <td>
                                    <span className={`status-badge ${report.status}`}>
                                      {report.status}
                                    </span>
                                  </td>
                                  <td>{report.totalChecked || 0}</td>
                                  <td className="success">{report.matched || 0}</td>
                                  <td className={report.discrepancies > 0 ? 'error' : ''}>
                                    {report.discrepancies || 0}
                                  </td>
                                  <td>{report.durationMs ? `${(report.durationMs / 1000).toFixed(1)}s` : 'N/A'}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        )}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* ==========================================
              FINANCIAL SUMMARY TAB - Actual Aggregate Data
              ========================================== */}
          {activeTab === 'financialSummary' && (
            <div className="financial-summary-content">
              <div className="summary-header-bar">
                <h2>📊 Financial Summary - Aggregate Data</h2>
                <button
                  className="btn btn-secondary refresh-btn"
                  onClick={fetchFinancialSummary}
                  disabled={financialSummaryLoading}
                >
                  {financialSummaryLoading ? '⟳ Refreshing...' : '↻ Refresh'}
                </button>
              </div>
              <p className="summary-intro">
                Real-time aggregate financial data using <strong>RestaurantFinancials.getBalance()</strong> and <strong>DeliveryFinancials.getBalance()</strong>.
                All calculations use <strong>integer arithmetic (paisa)</strong> | Last Updated: {financialSummary?.lastUpdated ? new Date(financialSummary.lastUpdated).toLocaleString('en-IN') : 'Loading...'}
              </p>

              {financialSummaryLoading ? (
                <div className="loading-spinner">Loading financial summary...</div>
              ) : financialSummary ? (
                <>
                  {/* Tax Rates Section (Static Reference) */}
                  <div className="guide-section rates-section">
                    <h3>⚙️ Centralized Tax & Fee Rates (Single Source of Truth)</h3>
                    <div className="rates-grid">
                      <div className="rate-card">
                        <div className="rate-header">
                          <span className="rate-icon">📋</span>
                          <span className="rate-title">TaxCalculator.js</span>
                        </div>
                        <div className="rate-items">
                          <div className="rate-item">
                            <span className="rate-label">TDS Rate (Section 194-O)</span>
                            <span className="rate-value">1%</span>
                            <span className="rate-desc">→ Deducted from restaurant earnings</span>
                          </div>
                          <div className="rate-item">
                            <span className="rate-label">Food GST Rate</span>
                            <span className="rate-value">5%</span>
                            <span className="rate-desc">→ Collected from customer, remitted by restaurant</span>
                          </div>
                          <div className="rate-item">
                            <span className="rate-label">Platform GST Rate</span>
                            <span className="rate-value">18%</span>
                            <span className="rate-desc">→ On platform fee (SAC 998319)</span>
                          </div>
                          <div className="rate-item">
                            <span className="rate-label">Delivery GST Rate</span>
                            <span className="rate-value">18%</span>
                            <span className="rate-desc">→ On delivery fee (platform's GST liability)</span>
                          </div>
                        </div>
                      </div>
                      <div className="rate-card">
                        <div className="rate-header">
                          <span className="rate-icon">⚡</span>
                          <span className="rate-title">InstantPayoutCalculator.js</span>
                        </div>
                        <div className="rate-items">
                          <div className="rate-item">
                            <span className="rate-label">Instant Payout Fee</span>
                            <span className="rate-value">5%</span>
                            <span className="rate-desc">→ Processing fee for instant withdrawals</span>
                          </div>
                          <div className="rate-item">
                            <span className="rate-label">Min Payout Amount</span>
                            <span className="rate-value">₹100</span>
                            <span className="rate-desc">→ Minimum instant payout</span>
                          </div>
                          <div className="rate-item">
                            <span className="rate-label">Max Payout Amount</span>
                            <span className="rate-value">₹50,000</span>
                            <span className="rate-desc">→ Maximum instant payout</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* STAGE 4: Restaurant Balance - ACTUAL DATA */}
                  <div className="guide-section balance-section">
                    <div className="stage-card actual-data">
                      <div className="stage-header">
                        <span className="stage-number">STAGE 4</span>
                        <h4>🏪 Restaurant Balance (RestaurantFinancials.getBalance()) - ACTUAL DATA</h4>
                        <span className="live-badge">LIVE DATA</span>
                      </div>
                      <div className="stage-content">
                        <p className="stage-desc">
                          <code>RestaurantFinancials.getBalance()</code> aggregates all restaurant earnings and payouts.
                          Below shows actual system-wide restaurant financial data:
                        </p>

                        <div className="actual-data-grid">
                          <div className="data-card highlight">
                            <span className="data-label">Total Restaurants</span>
                            <span className="data-value">{financialSummary.restaurants.totalCount}</span>
                          </div>
                          <div className="data-card success">
                            <span className="data-label">Ready for Payout</span>
                            <span className="data-value">{financialSummary.restaurants.readyForPayout}</span>
                          </div>
                          <div className="data-card warning">
                            <span className="data-label">Needs Bank Details</span>
                            <span className="data-value">{financialSummary.restaurants.needsBankDetails}</span>
                          </div>
                        </div>

                        <div className="balance-calculation actual">
                          <h5>📊 Aggregate Balance Calculation:</h5>
                          <div className="calc-steps">
                            <div className="calc-step">
                              <span className="step-label">Step 1: AGGREGATE ALL DELIVERED ORDER EARNINGS</span>
                              <code>totalNetEarningsPaisa = {Math.round(financialSummary.restaurants.totalNetEarnings * 100).toLocaleString()} paisa (₹{financialSummary.restaurants.totalNetEarnings.toLocaleString()})</code>
                            </div>
                            <div className="calc-step">
                              <span className="step-label">Step 2: Total TDS Deducted @ 0.1%</span>
                              <code>totalTdsPaisa ≈ {Math.round(financialSummary.restaurants.totalTdsDeducted * 100).toLocaleString()} paisa (₹{financialSummary.restaurants.totalTdsDeducted.toFixed(2)})</code>
                            </div>
                            <div className="calc-step">
                              <span className="step-label">Step 3: CALCULATE AVAILABLE BALANCE</span>
                              <code>availableBalance = netEarnings - (regularPayouts + instantPayouts)</code>
                            </div>
                          </div>

                          <table className="actual-summary-table">
                            <thead>
                              <tr>
                                <th>Metric</th>
                                <th>Value (Paisa)</th>
                                <th>Value (₹)</th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr>
                                <td>Total Pending Balance (All Restaurants)</td>
                                <td className="paisa">{Math.round(financialSummary.restaurants.totalPendingBalance * 100).toLocaleString()}</td>
                                <td className="rupees">₹{financialSummary.restaurants.totalPendingBalance.toLocaleString()}</td>
                              </tr>
                              <tr className="deduction">
                                <td>TDS Deducted (0.1% - Section 194-O)</td>
                                <td className="paisa">{Math.round(financialSummary.restaurants.totalTdsDeducted * 100).toLocaleString()}</td>
                                <td className="rupees">₹{financialSummary.restaurants.totalTdsDeducted.toFixed(2)}</td>
                              </tr>
                              <tr className="total-row">
                                <td><strong>TOTAL AVAILABLE FOR PAYOUT</strong></td>
                                <td className="paisa"><strong>{Math.round(financialSummary.restaurants.totalPendingBalance * 100).toLocaleString()}</strong></td>
                                <td className="rupees"><strong>₹{financialSummary.restaurants.totalPendingBalance.toLocaleString()}</strong></td>
                              </tr>
                            </tbody>
                          </table>
                        </div>

                        {/* Top Restaurants Pending Payout */}
                        {financialSummary.restaurants.byStatus.ready.length > 0 && (
                          <div className="top-recipients">
                            <h5>📋 Top Restaurants Ready for Payout:</h5>
                            <table className="recipients-table">
                              <thead>
                                <tr>
                                  <th>Restaurant</th>
                                  <th>Pending Balance</th>
                                  <th>Status</th>
                                </tr>
                              </thead>
                              <tbody>
                                {financialSummary.restaurants.byStatus.ready.slice(0, 5).map((r, idx) => (
                                  <tr key={idx}>
                                    <td>{r.name}</td>
                                    <td>₹{r.pendingAmount?.toLocaleString()}</td>
                                    <td><span className="status-badge ready">Ready</span></td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* STAGE 5: Regular Payout Flow - ACTUAL DATA */}
                    <div className="stage-card actual-data">
                      <div className="stage-header">
                        <span className="stage-number">STAGE 5A</span>
                        <h4>💰 Regular Payout Flow (Weekly Settlement) - ACTUAL DATA</h4>
                        <span className="live-badge">LIVE DATA</span>
                      </div>
                      <div className="stage-content">
                        <div className="actual-data-grid">
                          <div className="data-card">
                            <span className="data-label">Total Successful Payouts</span>
                            <span className="data-value">{financialSummary.payouts.totalPayouts}</span>
                          </div>
                          <div className="data-card restaurant">
                            <span className="data-label">Restaurant Payouts</span>
                            <span className="data-value">₹{financialSummary.payouts.restaurantPaidOut.toLocaleString()}</span>
                          </div>
                          <div className="data-card delivery">
                            <span className="data-label">Delivery Payouts</span>
                            <span className="data-value">₹{financialSummary.payouts.deliveryPaidOut.toLocaleString()}</span>
                          </div>
                          <div className="data-card highlight">
                            <span className="data-label">Total Paid Out</span>
                            <span className="data-value">₹{financialSummary.payouts.totalPaidOut.toLocaleString()}</span>
                          </div>
                        </div>

                        <div className="payout-process">
                          <h5>📝 Payout Process Steps:</h5>
                          <div className="process-steps">
                            <div className="process-step">
                              <span className="step-icon">1️⃣</span>
                              <strong>CREATE PAYOUT RECORD</strong>
                              <p>Admin initiates payout for available balance</p>
                            </div>
                            <div className="process-step">
                              <span className="step-icon">2️⃣</span>
                              <strong>EXECUTE VIA CASHFREE</strong>
                              <p>Create beneficiary → Request bank transfer</p>
                            </div>
                            <div className="process-step">
                              <span className="step-icon">3️⃣</span>
                              <strong>UPDATE ORDER STATUS</strong>
                              <p>Mark orders as <code>payoutStatus.status = 'paid'</code></p>
                            </div>
                            <div className="process-step">
                              <span className="step-icon">4️⃣</span>
                              <strong>GENERATE SETTLEMENT STATEMENT</strong>
                              <p>invoiceController.onPayoutCompleted()</p>
                            </div>
                          </div>
                        </div>

                        {/* Recent Payouts */}
                        {recentPayouts.length > 0 && (
                          <div className="recent-payouts">
                            <h5>📋 Recent Successful Payouts:</h5>
                            <table className="payouts-table">
                              <thead>
                                <tr>
                                  <th>Recipient</th>
                                  <th>Type</th>
                                  <th>Amount</th>
                                  <th>Date</th>
                                </tr>
                              </thead>
                              <tbody>
                                {recentPayouts.slice(0, 5).map((p, idx) => (
                                  <tr key={idx}>
                                    <td>{p.recipientName || p.recipientId}</td>
                                    <td>{p.recipientType === 'restaurant' ? '🏪' : '🚴'} {p.recipientType}</td>
                                    <td>₹{p.amount?.toLocaleString()}</td>
                                    <td>{new Date(p.completedAt || p.createdAt).toLocaleDateString('en-IN')}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* STAGE 5B: Instant Payout - ACTUAL DATA */}
                    <div className="stage-card instant-payout actual-data">
                      <div className="stage-header">
                        <span className="stage-number">STAGE 5B</span>
                        <h4>⚡ Instant Payout (5% Fee) - ACTUAL DATA</h4>
                        <span className="live-badge">LIVE DATA</span>
                      </div>
                      <div className="stage-content">
                        <div className="actual-data-grid">
                          <div className="data-card">
                            <span className="data-label">Total Requests</span>
                            <span className="data-value">{financialSummary.instant.totalRequests}</span>
                          </div>
                          <div className="data-card warning">
                            <span className="data-label">Pending Approval</span>
                            <span className="data-value">{financialSummary.instant.pending.length}</span>
                          </div>
                          <div className="data-card processing">
                            <span className="data-label">Processing</span>
                            <span className="data-value">{financialSummary.instant.processing.length}</span>
                          </div>
                          <div className="data-card success">
                            <span className="data-label">Completed</span>
                            <span className="data-value">{financialSummary.instant.completed.length}</span>
                          </div>
                        </div>

                        <div className="instant-calc-summary">
                          <h5>📊 Instant Payout Summary (5% Fee Calculation):</h5>
                          <table className="actual-summary-table">
                            <tbody>
                              <tr>
                                <td>Total Requested Amount (Active)</td>
                                <td className="paisa">{Math.round(financialSummary.instant.totalRequestedAmount * 100).toLocaleString()} paisa</td>
                                <td className="rupees">₹{financialSummary.instant.totalRequestedAmount.toLocaleString()}</td>
                              </tr>
                              <tr className="deduction">
                                <td>Processing Fees Collected (5%)</td>
                                <td className="paisa">{Math.round(financialSummary.instant.totalFeesCollected * 100).toLocaleString()} paisa</td>
                                <td className="rupees">₹{financialSummary.instant.totalFeesCollected.toLocaleString()}</td>
                              </tr>
                              <tr className="net-row">
                                <td><strong>Net Amount Paid Out</strong></td>
                                <td className="paisa">{Math.round(financialSummary.instant.totalNetPaid * 100).toLocaleString()} paisa</td>
                                <td className="rupees">₹{financialSummary.instant.totalNetPaid.toLocaleString()}</td>
                              </tr>
                            </tbody>
                          </table>

                          <div className="fee-example">
                            <h6>💡 Fee Calculation Example:</h6>
                            <p>If restaurant requests ₹1,000 instant payout:</p>
                            <code>
                              requestedPaisa = 1000 × 100 = 100,000 paisa<br/>
                              feePaisa = 100,000 × 5% = 5,000 paisa (₹50)<br/>
                              netPaisa = 100,000 - 5,000 = 95,000 paisa (₹950 received)
                            </code>
                          </div>
                        </div>

                        {/* Pending Instant Requests */}
                        {financialSummary.instant.pending.length > 0 && (
                          <div className="pending-requests">
                            <h5>⏳ Pending Instant Payout Requests:</h5>
                            <table className="requests-table">
                              <thead>
                                <tr>
                                  <th>Recipient</th>
                                  <th>Type</th>
                                  <th>Requested</th>
                                  <th>Fee (5%)</th>
                                  <th>Net Amount</th>
                                </tr>
                              </thead>
                              <tbody>
                                {financialSummary.instant.pending.slice(0, 5).map((r, idx) => (
                                  <tr key={idx}>
                                    <td>{r.restaurantName || r.deliveryPartnerName || 'N/A'}</td>
                                    <td>{r.recipientType === 'restaurant' ? '🏪' : '🚴'}</td>
                                    <td>₹{r.requestedAmount?.toLocaleString()}</td>
                                    <td className="fee">-₹{r.processingFee?.toLocaleString()}</td>
                                    <td className="net">₹{r.netAmount?.toLocaleString()}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* STAGE 6: Delivery Partner Balance - ACTUAL DATA */}
                    <div className="stage-card actual-data">
                      <div className="stage-header">
                        <span className="stage-number">STAGE 6</span>
                        <h4>🚴 Delivery Partner Balance (DeliveryFinancials.getBalance()) - ACTUAL DATA</h4>
                        <span className="live-badge">LIVE DATA</span>
                      </div>
                      <div className="stage-content">
                        <p className="stage-desc">
                          <code>DeliveryFinancials.getBalance()</code> aggregates delivery earnings (10% commission deducted) + tips + incentives - penalties.
                        </p>

                        <div className="actual-data-grid">
                          <div className="data-card highlight">
                            <span className="data-label">Total Delivery Partners</span>
                            <span className="data-value">{financialSummary.delivery.totalCount}</span>
                          </div>
                          <div className="data-card success">
                            <span className="data-label">Ready for Payout</span>
                            <span className="data-value">{financialSummary.delivery.readyForPayout}</span>
                          </div>
                          <div className="data-card warning">
                            <span className="data-label">Needs Bank Details</span>
                            <span className="data-value">{financialSummary.delivery.needsBankDetails}</span>
                          </div>
                        </div>

                        <div className="balance-calculation actual">
                          <h5>📊 Aggregate Balance Calculation:</h5>
                          <div className="calc-steps">
                            <div className="calc-step">
                              <span className="step-label">Step 1: AGGREGATE ORDER EARNINGS (after 10% commission)</span>
                              <code>totalNetEarningsPaisa = {Math.round(financialSummary.delivery.totalNetEarnings * 100).toLocaleString()} paisa (₹{financialSummary.delivery.totalNetEarnings.toLocaleString()})</code>
                            </div>
                            <div className="calc-step">
                              <span className="step-label">Step 2: Platform Commission Collected (10%)</span>
                              <code>totalCommissionPaisa ≈ {Math.round(financialSummary.delivery.totalCommission * 100).toLocaleString()} paisa (₹{financialSummary.delivery.totalCommission.toFixed(2)})</code>
                            </div>
                            <div className="calc-step">
                              <span className="step-label">Step 3: + Slot Incentives - Penalties (from DeliverySlotRecord)</span>
                              <code>balance += incentives - penalties</code>
                            </div>
                            <div className="calc-step">
                              <span className="step-label">Step 4: CALCULATE AVAILABLE BALANCE</span>
                              <code>availableBalance = netEarnings + tips + incentives - penalties - payouts</code>
                            </div>
                          </div>

                          <table className="actual-summary-table">
                            <thead>
                              <tr>
                                <th>Metric</th>
                                <th>Value (Paisa)</th>
                                <th>Value (₹)</th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr>
                                <td>Total Pending Balance (All Partners)</td>
                                <td className="paisa">{Math.round(financialSummary.delivery.totalPendingBalance * 100).toLocaleString()}</td>
                                <td className="rupees">₹{financialSummary.delivery.totalPendingBalance.toLocaleString()}</td>
                              </tr>
                              <tr className="deduction">
                                <td>Commission Collected (10%)</td>
                                <td className="paisa">{Math.round(financialSummary.delivery.totalCommission * 100).toLocaleString()}</td>
                                <td className="rupees">₹{financialSummary.delivery.totalCommission.toFixed(2)}</td>
                              </tr>
                              <tr className="total-row">
                                <td><strong>TOTAL AVAILABLE FOR PAYOUT</strong></td>
                                <td className="paisa"><strong>{Math.round(financialSummary.delivery.totalPendingBalance * 100).toLocaleString()}</strong></td>
                                <td className="rupees"><strong>₹{financialSummary.delivery.totalPendingBalance.toLocaleString()}</strong></td>
                              </tr>
                            </tbody>
                          </table>
                        </div>

                        {/* Top Delivery Partners Pending Payout */}
                        {financialSummary.delivery.byStatus.ready.length > 0 && (
                          <div className="top-recipients">
                            <h5>📋 Top Delivery Partners Ready for Payout:</h5>
                            <table className="recipients-table">
                              <thead>
                                <tr>
                                  <th>Delivery Partner</th>
                                  <th>Pending Balance</th>
                                  <th>Status</th>
                                </tr>
                              </thead>
                              <tbody>
                                {financialSummary.delivery.byStatus.ready.slice(0, 5).map((d, idx) => (
                                  <tr key={idx}>
                                    <td>{d.name}</td>
                                    <td>₹{d.pendingAmount?.toLocaleString()}</td>
                                    <td><span className="status-badge ready">Ready</span></td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Order Summary Stats */}
                  {financialSummary.orders && (
                    <div className="guide-section orders-summary">
                      <h3>📦 Order Earnings Overview</h3>
                      <div className="orders-stats-grid">
                        <div className="order-stat">
                          <span className="stat-label">Total Orders</span>
                          <span className="stat-value">{financialSummary.orders.orderCount?.toLocaleString() || 0}</span>
                        </div>
                        <div className="order-stat">
                          <span className="stat-label">Customer Paid</span>
                          <span className="stat-value">₹{financialSummary.orders.totalCustomerPaid?.toLocaleString() || 0}</span>
                        </div>
                        <div className="order-stat restaurant">
                          <span className="stat-label">Restaurant Earnings</span>
                          <span className="stat-value">₹{financialSummary.orders.totalRestaurantEarnings?.toLocaleString() || 0}</span>
                        </div>
                        <div className="order-stat delivery">
                          <span className="stat-label">Delivery Earnings</span>
                          <span className="stat-value">₹{financialSummary.orders.totalDeliveryEarnings?.toLocaleString() || 0}</span>
                        </div>
                        <div className="order-stat platform">
                          <span className="stat-label">Platform Earnings</span>
                          <span className="stat-value">₹{financialSummary.orders.totalPlatformEarnings?.toLocaleString() || 0}</span>
                        </div>
                        <div className="order-stat tds">
                          <span className="stat-label">TDS Collected</span>
                          <span className="stat-value">₹{financialSummary.orders.totalTdsCollected?.toLocaleString() || 0}</span>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Key Points Section */}
                  <div className="guide-section key-points-section">
                    <h3>✅ Key Points - Production Financial Flow</h3>
                    <div className="key-points-grid">
                      <div className="key-point">
                        <span className="point-num">1</span>
                        <strong>All money calculations use PAISA (integer arithmetic)</strong>
                        <p>via MoneyUtils.js to prevent floating-point errors</p>
                      </div>
                      <div className="key-point">
                        <span className="point-num">2</span>
                        <strong>Earnings are calculated ONCE at delivery time</strong>
                        <p>and stored in the order document - never recalculated</p>
                      </div>
                      <div className="key-point">
                        <span className="point-num">3</span>
                        <strong>TDS (0.1%) is estimated per order, deducted at payout level</strong>
                        <p>Actual TDS calculated on aggregate gross at payout time (Section 194-O)</p>
                      </div>
                      <div className="key-point">
                        <span className="point-num">4</span>
                        <strong>GST on food (5%) is collected from customer</strong>
                        <p>and passed through to restaurant (they remit to govt)</p>
                      </div>
                      <div className="key-point">
                        <span className="point-num">5</span>
                        <strong>GST on platform fee (18%) is platform's liability</strong>
                        <p>collected and remitted by platform</p>
                      </div>
                      <div className="key-point">
                        <span className="point-num">6</span>
                        <strong>GST on delivery fee (18%) is platform's liability</strong>
                        <p>collected and remitted by platform</p>
                      </div>
                      <div className="key-point">
                        <span className="point-num">7</span>
                        <strong>Delivery partner commission (10%)</strong>
                        <p>is deducted from delivery fee</p>
                      </div>
                      <div className="key-point">
                        <span className="point-num">8</span>
                        <strong>Restaurant commission (0%)</strong>
                        <p>zero commission model currently</p>
                      </div>
                      <div className="key-point">
                        <span className="point-num">9</span>
                        <strong>Instant payouts use requestedAmount</strong>
                        <p>(not netAmount) for balance calculation</p>
                      </div>
                      <div className="key-point">
                        <span className="point-num">10</span>
                        <strong>All rates come from centralized TaxCalculator.js</strong>
                        <p>single source of truth</p>
                      </div>
                    </div>
                  </div>

                  {/* Code References */}
                  <div className="guide-section code-refs-section">
                    <h3>📁 Key Files Reference</h3>
                    <table className="code-refs-table">
                      <thead>
                        <tr>
                          <th>Component</th>
                          <th>File</th>
                          <th>Key Function</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td>Tax Rates</td>
                          <td><code>TaxCalculator.js:15-20</code></td>
                          <td>getDefaultRates()</td>
                        </tr>
                        <tr>
                          <td>Instant Payout Settings</td>
                          <td><code>InstantPayoutCalculator.js:15-18</code></td>
                          <td>getDefaultSettings()</td>
                        </tr>
                        <tr>
                          <td>Integer Arithmetic</td>
                          <td><code>moneyUtils.js:18-280</code></td>
                          <td>toPaisa(), percentage()</td>
                        </tr>
                        <tr>
                          <td>Earnings Calculation</td>
                          <td><code>Order.js:393-551</code></td>
                          <td>calculateAndStoreEarnings()</td>
                        </tr>
                        <tr>
                          <td>Restaurant Balance</td>
                          <td><code>RestaurantFinancials.js:33-153</code></td>
                          <td>getBalance()</td>
                        </tr>
                        <tr>
                          <td>Delivery Balance</td>
                          <td><code>DeliveryFinancials.js:34-175</code></td>
                          <td>getBalance()</td>
                        </tr>
                        <tr>
                          <td>Payout Processing</td>
                          <td><code>payoutController.js:505-659</code></td>
                          <td>createPayout()</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </>
              ) : (
                <div className="no-data">No financial data available. Click refresh to load.</div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Create Payout Modal */}
      {showPayoutModal && selectedRecipient && (
        <div className="modal-overlay" onClick={() => setShowPayoutModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>💰 Create Payout</h3>
              <button className="close-btn" onClick={() => setShowPayoutModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="recipient-info">
                <p><strong>Recipient:</strong> {selectedRecipient.name}</p>
                <p><strong>Type:</strong> {selectedRecipient.type === 'restaurant' ? '🏪 Restaurant' : '🚴 Delivery Partner'}</p>
                <p><strong>Pending Amount:</strong> ₹{(selectedRecipient.type === 'restaurant' ? (selectedRecipient.grossPendingAmount ?? selectedRecipient.pendingAmount) : selectedRecipient.pendingAmount)?.toLocaleString()}</p>
              </div>
              <div className="form-group">
                <label>Payout Amount (₹)</label>
                <input
                  type="number"
                  value={payoutAmount}
                  onChange={(e) => setPayoutAmount(e.target.value)}
                  placeholder="Enter amount"
                  min="1"
                />
              </div>
              <div className="form-group">
                <label>Description (Optional)</label>
                <textarea
                  value={payoutDescription}
                  onChange={(e) => setPayoutDescription(e.target.value)}
                  placeholder="Enter description or notes"
                  rows="3"
                />
              </div>

              {/* Settlement Fee Breakdown (Restaurant only) - DEDUCTED from payout */}
              {selectedRecipient.type === 'restaurant' && payoutAmount && parseFloat(payoutAmount) > 0 && (
                <div className="settlement-fee-breakdown" style={{
                  marginTop: '16px',
                  padding: '16px',
                  background: 'linear-gradient(135deg, #fff8f0 0%, #fff5f5 100%)',
                  borderRadius: '12px',
                  border: '1px solid #ffddcc'
                }}>
                  <h4 style={{ margin: '0 0 12px 0', color: '#d84315', fontSize: '14px', fontWeight: '600' }}>
                    💳 {selectedRecipient.billingModel?.type === 'commission' ? 'Platform Fee' : 'Settlement Fee'} Breakdown
                  </h4>
                  {(() => {
                    const feeCalc = calculateSettlementFee(payoutAmount, selectedRecipient.billingModel);
                    return (
                      <div style={{ fontSize: '13px', color: '#555' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                          <span>Gross Payout Amount:</span>
                          <span style={{ fontWeight: '500' }}>₹{feeCalc.payoutAmount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px', color: '#d84315' }}>
                          <span>TDS ({TDS_RATE_194O}%, Sec 194-O):</span>
                          <span>- ₹{feeCalc.tds.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px', color: '#d84315' }}>
                          <span>{feeCalc.feeType === 'commission' ? `Platform Fee (${feeCalc.rate}% of gross${feeCalc.gstMode === 'inclusive' ? ', incl. GST' : ''})` : `Settlement Fee (${SETTLEMENT_FEE_RATE}% of post-TDS)`}:</span>
                          <span>- ₹{feeCalc.settlementFee.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px', paddingLeft: '12px', fontSize: '12px', color: '#888' }}>
                          <span>CGST (9%):</span>
                          <span>- ₹{feeCalc.cgst.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px', paddingLeft: '12px', fontSize: '12px', color: '#888' }}>
                          <span>SGST (9%):</span>
                          <span>- ₹{feeCalc.sgst.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', color: '#d84315', fontWeight: '500', borderTop: '1px dashed #ffccbb', paddingTop: '6px' }}>
                          <span>Total Deducted (TDS + Fee):</span>
                          <span>- ₹{feeCalc.totalDeducted.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: '600', fontSize: '15px', color: '#2e7d32', background: '#e8f5e9', padding: '8px 12px', borderRadius: '8px', marginTop: '8px' }}>
                          <span>🏦 Net Payout to Restaurant:</span>
                          <span>₹{feeCalc.netPayout.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                        </div>
                        <p style={{ fontSize: '11px', color: '#888', marginTop: '10px', marginBottom: '0', fontStyle: 'italic' }}>
                          ℹ️ {feeCalc.feeType === 'commission' ? 'Platform fee' : 'Settlement fee'} invoice will be generated automatically
                        </p>
                      </div>
                    );
                  })()}
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowPayoutModal(false)}>
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={handleCreatePayout}
                disabled={loading || !payoutAmount}
              >
                {loading ? 'Creating...' : '💰 Create Payout'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bank Details Modal */}
      {showBankModal && selectedRecipient && (
        <div className="modal-overlay" onClick={() => setShowBankModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>🏦 Bank Details</h3>
              <button className="close-btn" onClick={() => setShowBankModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="recipient-info">
                <p><strong>Recipient:</strong> {selectedRecipient.name}</p>
                {(() => {
                  const bankData = selectedRecipient.type === 'restaurant'
                    ? selectedRecipient.bankDetails
                    : selectedRecipient.deliveryPartner?.bankDetails;
                  // Bank verification disabled - show simple status
                  return bankData?.accountNumber ? (
                    <div className="verification-status" style={{ background: '#e3f2fd', padding: '8px 12px', borderRadius: '6px' }}>
                      <span className="status-icon">ℹ️</span>
                      <span className="status-text" style={{ marginLeft: '8px' }}>
                        Bank details saved (Verification disabled - Manual payout mode)
                      </span>
                    </div>
                  ) : null;
                })()}
              </div>
              <div className="form-group">
                <label>Account Holder Name *</label>
                <input
                  type="text"
                  value={bankDetails.accountHolderName}
                  onChange={(e) => setBankDetails({ ...bankDetails, accountHolderName: e.target.value })}
                  placeholder="Enter account holder name"
                />
              </div>
              <div className="form-group">
                <label>Bank Account Number *</label>
                <input
                  type="text"
                  value={bankDetails.accountNumber}
                  onChange={(e) => setBankDetails({ ...bankDetails, accountNumber: e.target.value })}
                  placeholder="Enter account number"
                />
              </div>
              <div className="form-group">
                <label>IFSC Code *</label>
                <input
                  type="text"
                  value={bankDetails.ifscCode}
                  onChange={(e) => setBankDetails({ ...bankDetails, ifscCode: e.target.value.toUpperCase() })}
                  placeholder="Enter IFSC code"
                />
              </div>
              <div className="form-group">
                <label>Bank Name</label>
                <input
                  type="text"
                  value={bankDetails.bankName}
                  onChange={(e) => setBankDetails({ ...bankDetails, bankName: e.target.value })}
                  placeholder="Enter bank name"
                />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowBankModal(false)}>
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={handleUpdateBankDetails}
                disabled={loading || !bankDetails.accountNumber || !bankDetails.ifscCode}
              >
                {loading ? 'Saving...' : '💾 Save Bank Details'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reject Instant Payout Modal */}
      {showRejectModal && selectedRequest && (
        <div className="modal-overlay" onClick={() => setShowRejectModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>❌ Reject Instant Payout Request</h3>
              <button className="close-btn" onClick={() => setShowRejectModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="recipient-info">
                <p><strong>Request ID:</strong> {selectedRequest.requestId}</p>
                <p><strong>Partner:</strong> {selectedRequest.partnerName}</p>
                <p><strong>Amount Requested:</strong> ₹{selectedRequest.requestedAmount?.toLocaleString()}</p>
              </div>
              <div className="form-group">
                <label>Rejection Reason *</label>
                <textarea
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  placeholder="Enter reason for rejection (e.g., Insufficient verification, Invalid bank details, etc.)"
                  rows="3"
                />
              </div>
              <div className="warning-box">
                <span className="warning-icon">⚠️</span>
                <span>The partner will be notified of this rejection and the reason provided.</span>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowRejectModal(false)}>
                Cancel
              </button>
              <button
                className="btn btn-danger"
                onClick={handleRejectInstantPayout}
                disabled={loading || !rejectionReason.trim()}
              >
                {loading ? 'Rejecting...' : '❌ Reject Request'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Manual Payment Confirmation Modal */}
      {showManualPaymentModal && manualPaymentData.payoutId && (
        <div className="modal-overlay" onClick={() => !confirmingPayment && setShowManualPaymentModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: '550px' }}>
            <div className="modal-header">
              <h3>💰 Confirm Manual Payment</h3>
              <button
                className="close-btn"
                onClick={() => setShowManualPaymentModal(false)}
                disabled={confirmingPayment}
              >×</button>
            </div>
            <div className="modal-body">
              {manualPaymentData.payout && (
                <>
                  <div className="recipient-info" style={{ marginBottom: '20px' }}>
                    <p><strong>Recipient:</strong> {manualPaymentData.payout.recipientName}</p>
                    <p><strong>Amount:</strong> <span style={{ fontSize: '1.3em', color: '#2e7d32', fontWeight: 'bold' }}>₹{manualPaymentData.payout.amount?.toLocaleString()}</span></p>
                    <p><strong>Purpose:</strong> {manualPaymentData.payout.purpose?.replace(/_/g, ' ')}</p>
                  </div>

                  {manualPaymentData.payout.bankAccount && (
                    <div style={{
                      background: '#f5f5f5',
                      padding: '15px',
                      borderRadius: '8px',
                      marginBottom: '20px',
                      border: '1px solid #ddd'
                    }}>
                      <h4 style={{ margin: '0 0 12px 0', color: '#333' }}>🏦 Bank Details for Transfer</h4>
                      <div style={{ display: 'grid', gap: '8px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span><strong>Account Holder:</strong> {manualPaymentData.payout.bankAccount.accountHolderName}</span>
                          <button
                            onClick={() => copyToClipboard(manualPaymentData.payout.bankAccount.accountHolderName, 'Account holder name')}
                            style={{ padding: '4px 8px', fontSize: '12px', cursor: 'pointer' }}
                          >📋 Copy</button>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span><strong>Account Number:</strong> {manualPaymentData.payout.bankAccount.accountNumber}</span>
                          <button
                            onClick={() => copyToClipboard(manualPaymentData.payout.bankAccount.accountNumber, 'Account number')}
                            style={{ padding: '4px 8px', fontSize: '12px', cursor: 'pointer' }}
                          >📋 Copy</button>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span><strong>IFSC Code:</strong> {manualPaymentData.payout.bankAccount.ifscCode}</span>
                          <button
                            onClick={() => copyToClipboard(manualPaymentData.payout.bankAccount.ifscCode, 'IFSC code')}
                            style={{ padding: '4px 8px', fontSize: '12px', cursor: 'pointer' }}
                          >📋 Copy</button>
                        </div>
                        {manualPaymentData.payout.bankAccount.bankName && (
                          <span><strong>Bank:</strong> {manualPaymentData.payout.bankAccount.bankName}</span>
                        )}
                      </div>
                    </div>
                  )}
                </>
              )}

              <div className="form-group">
                <label>Transfer Mode *</label>
                <select
                  value={manualPaymentData.transferMode}
                  onChange={(e) => setManualPaymentData(prev => ({ ...prev, transferMode: e.target.value }))}
                  style={{ width: '100%', padding: '10px', borderRadius: '4px', border: '1px solid #ddd' }}
                >
                  <option value="imps">IMPS (Immediate)</option>
                  <option value="neft">NEFT</option>
                  <option value="rtgs">RTGS</option>
                  <option value="upi">UPI</option>
                  <option value="banktransfer">Bank Transfer</option>
                </select>
              </div>

              <div className="form-group">
                <label>UTR Number * (Unique Transaction Reference)</label>
                <input
                  type="text"
                  value={manualPaymentData.utr}
                  onChange={(e) => setManualPaymentData(prev => ({ ...prev, utr: e.target.value }))}
                  placeholder="Enter UTR from your bank transfer"
                  style={{ width: '100%', padding: '10px', borderRadius: '4px', border: '1px solid #ddd' }}
                  autoFocus
                />
                <small style={{ color: '#666' }}>You can find UTR in your bank app/statement after transfer</small>
              </div>

              <div className="form-group">
                <label>Remarks (Optional)</label>
                <input
                  type="text"
                  value={manualPaymentData.remarks}
                  onChange={(e) => setManualPaymentData(prev => ({ ...prev, remarks: e.target.value }))}
                  placeholder="Any additional notes"
                  style={{ width: '100%', padding: '10px', borderRadius: '4px', border: '1px solid #ddd' }}
                />
              </div>

              <div className="warning-box" style={{ background: '#e3f2fd', border: '1px solid #90caf9' }}>
                <span className="warning-icon">ℹ️</span>
                <span>Please ensure you have already transferred the money via your bank app before confirming.</span>
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="btn btn-secondary"
                onClick={() => setShowManualPaymentModal(false)}
                disabled={confirmingPayment}
              >
                Cancel
              </button>
              <button
                className="btn btn-success"
                onClick={handleConfirmManualPayment}
                disabled={confirmingPayment || !manualPaymentData.utr.trim()}
                style={{ background: '#2e7d32' }}
              >
                {confirmingPayment ? 'Confirming...' : '✓ Confirm Payment'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PayoutManagement;
